import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Volume2,
  ChevronRight,
  ChevronLeft,
  BookOpen,
  Languages,
  Lightbulb,
  Trophy,
  CheckCircle,
  XCircle,
  Puzzle,
  GraduationCap,
  ArrowRight,
  Type,
  Home,
  Split,
  ListChecks,
  Star,
  Settings,
  RotateCcw,
  Menu as MenuIcon, // 名前衝突を避けるためリネーム
  PenTool,
  LogOut,
  Mic,
  Play,
  Loader2,
  Square,
  Pause,
  Key,
  User,
  Activity,
  X, // 閉じるボタン用
  Dumbbell, // 追加: 練習アイコン用
  HelpCircle, // 追加: ヘルプアイコン用
  Eye, // 追加: 表示用
  EyeOff, // 追加: 非表示用
  Sparkles, // 追加: 高品質音声用アイコン
  Wifi, // 追加: 通信アイコン
  WifiOff, // 追加: オフラインアイコン
  Globe, // 追加: 言語切り替え用アイコン
  RefreshCw, // 追加: 更新用アイコン
} from "lucide-react";

// ==============================================================================
// 🚀【本番移行ガイド / DEPLOYMENT GUIDE】🚀
//
// このアプリは現在「プレビューモード」で動作しています。
// Vercel等の本番環境へデプロイする直前に、以下の STEP 1 〜 STEP 3 を実施してください。
// ==============================================================================

// ==============================================================================
// 【STEP 1】 外部データ読み込み設定
// ==============================================================================

// 🔴 本番作業: 以下の import のコメントアウト「//」を外して有効化してください。
import { getStoryById } from "./data/index";

// 🔵 本番作業: 以下の「プレビュー用ダミー関数」ブロックを【削除】してください。
// ▼▼▼ DELETE START (ここから削除) ▼▼▼
/*
const getStoryById = (id) => {
  // プレビュー環境ではローカル定数 STORY_DATABASE から検索
  if (!id) return null;
  return STORY_DATABASE.find((story) => story.id === id) || null;
};
*/
// ▲▲▲ DELETE END (ここまで削除) ▲▲▲

// ==============================================================================
// 【STEP 2】 Clerk 認証設定
// ==============================================================================

// 🔴 本番作業: 以下の import ブロックのコメントアウト「/* */」を外して有効化してください。

import {
  ClerkProvider,
  SignedIn,
  SignedOut,
  SignInButton,
  UserButton,
  useAuth,
  useUser,
  SignIn,
  SignUp,
} from "@clerk/clerk-react";

// FIX: ブラウザプレビューでの ReferenceError 回避のため、キーを直接記述
// 本番環境では環境変数 process.env.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY を使用してください
const clerkPubKey = "pk_test_Z2FtZS10ZXJtaXRlLTg3LmNsZXJrLmFjY291bnRzLmRldiQ";

// 🔵 本番作業: 以下の「プレビュー用ダミーコンポーネント」ブロックを【削除】してください。
// ▼▼▼ DELETE START (ここから削除) ▼▼▼
/*
// プレビュー用の簡易認証コンテキスト
const PreviewAuthContext = React.createContext(null);

const MOCK_USER = {
  fullName: "Preview User",
  firstName: "Preview",
  publicMetadata: { plan: "free" },
};

const mockGetToken = async () => "dummy_preview_token";

const ClerkProvider = ({ children }) => {
  // デフォルトは未ログイン(Guest)
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [user, setUser] = useState(null);

  const handleSignIn = () => {
    setIsSignedIn(true);
    setUser(MOCK_USER);
  };

  const handleSignOut = () => {
    setIsSignedIn(false);
    setUser(null);
  };

  return (
    <PreviewAuthContext.Provider
      value={{ isSignedIn, user, handleSignIn, handleSignOut }}
    >
      {children}
    </PreviewAuthContext.Provider>
  );
};

const useAuth = () => {
  const context = React.useContext(PreviewAuthContext);
  if (!context)
    return {
      isLoaded: true,
      isSignedIn: false,
      userId: null,
      getToken: mockGetToken,
      signOut: async () => {},
    };
  return {
    isLoaded: true,
    isSignedIn: context.isSignedIn,
    userId: context.isSignedIn ? "preview_user_id" : null,
    getToken: mockGetToken,
    signOut: async () => context.handleSignOut(),
  };
};

const useUser = () => {
  const context = React.useContext(PreviewAuthContext);
  return {
    user: context ? context.user : null,
  };
};

const SignedIn = ({ children }) => {
  const { isSignedIn } = useAuth();
  return isSignedIn ? <>{children}</> : null;
};

const SignedOut = ({ children }) => {
  const { isSignedIn } = useAuth();
  return !isSignedIn ? <>{children}</> : null;
};

const SignInButton = () => {
  const context = React.useContext(PreviewAuthContext);
  return (
    <button
      onClick={context?.handleSignIn}
      disabled={disabled}
      className={`px-4 py-2 rounded-lg font-bold text-xs transition-colors ${
        disabled 
          ? "bg-slate-300 text-slate-500 cursor-not-allowed shadow-none" 
          : "bg-slate-800 text-white hover:bg-slate-700"
      }`}
    >      Sign In (Mock)
    </button>
  );
};

const UserButton = () => {
  const { signOut } = useAuth();
  return (
    <button
      onClick={signOut}
      className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center text-slate-500 border border-slate-300 hover:bg-slate-300 transition-colors"
      title="Sign Out (Mock)"
    >
      <User size={16} />
    </button>
  );
};
*/
// ▲▲▲ DELETE END (ここまで削除) ▲▲▲

// ==============================================================================
// 【SAMPLE DATA】 プレビュー用データ
// 本番環境では外部ファイルから読み込むため、本番時には不要になりますが、
// エラー回避のために残しておいても問題ありません（STEP 1で関数を差し替えるため）。
// ==============================================================================
const STORY_DATABASE = [
  {
    id: "sample",
    title: "The Magic Seed (Sample)",
    level: "LEVEL 2",
    category: "Fantasy",
    content: [
      {
        title: "Part 1: The Blue Seed",
        rubyText: [
          { w: "I", r: "アイ" },
          { w: "found", r: "ファウンド" },
          { w: "a", r: "ア" },
          { w: "blue", r: "ブルー" },
          { w: "seed", r: "シード", s: true },
          { w: "in", r: "イン" },
          { w: "the", r: "ザ" },
          { w: "park.", r: "パーク" },
          { w: "It", r: "イット" },
          { w: "was", r: "ワズ" },
          { w: "shining", r: "シャイニング", s: true },
          { w: "like", r: "ライク", s: true },
          { w: "a", r: "ア" },
          { w: "star.", r: "スター" },
          { w: "My", r: "マイ" },
          { w: "grandfather", r: "グランドファーザー" },
          { w: "said,", r: "セッド", s: true },
          { w: "'This", r: "ディス" },
          { w: "is", r: "イズ" },
          { w: "a", r: "ア" },
          { w: "magic", r: "マジック" },
          { w: "seed.'", r: "シード" },
          { w: "I", r: "アイ" },
          { w: "planted", r: "プランテッド" },
          { w: "it", r: "イット", s: true },
          { w: "in", r: "イン" },
          { w: "a", r: "ア" },
          { w: "small", r: "スモール" },
          { w: "pot.", r: "ポット" },
        ],
        en: "I found a blue seed in the park. It was shining like a star. My grandfather said, 'This is a magic seed.' I planted it in a small pot.",
        ja: "公園で青い種を見つけました。それは星のように輝いていました。おじいちゃんは「これは魔法の種だよ」と言いました。僕はそれを小さな鉢に植えました。",
        learning: {
          vocab: [
            { word: "seed", mean: "種" },
            { word: "shining", mean: "輝いている" },
            { word: "plant", mean: "植える" },
          ],
          grammar: {
            title: "like + 名詞",
            desc: "「～のような」という意味の前置詞です。",
            key: "shining like a star",
          },
        },
        color: "bg-blue-100",
        accent: "text-blue-600",
        mood: { rate: 1.0, pitch: 1.0 },
        image: "images/sample/01.jpg",
        startTime: 0,
      },
      {
        title: "Part 2: The Golden Flower",
        rubyText: [
          { w: "The", r: "ザ" },
          { w: "next", r: "ネクスト" },
          { w: "morning,", r: "モーニング", s: true },
          { w: "a", r: "ア" },
          { w: "golden", r: "ゴールデン" },
          { w: "flower", r: "フラワー" },
          { w: "bloomed.", r: "ブルームド" },
          { w: "It", r: "イット" },
          { w: "was", r: "ワズ" },
          { w: "very", r: "ベリー" },
          { w: "beautiful.", r: "ビューティフル" },
          { w: "The", r: "ザ" },
          { w: "flower", r: "フラワー" },
          { w: "sang", r: "サング" },
          { w: "a", r: "ア" },
          { w: "quiet", r: "クワイエット" },
          { w: "song.", r: "ソング" },
          { w: "I", r: "アイ" },
          { w: "was", r: "ワズ" },
          { w: "surprised.", r: "サプライズド" },
          { w: "The", r: "ザ" },
          { w: "song", r: "ソング" },
          { w: "made", r: "メイド" },
          { w: "me", r: "ミー", s: true },
          { w: "feel", r: "フィール" },
          { w: "happy.", r: "ハッピー" },
        ],
        en: "The next morning, a golden flower bloomed. It was very beautiful. The flower sang a quiet song. I was surprised. The song made me feel happy.",
        ja: "翌朝、金色の花が咲きました。とても綺麗でした。花は静かな歌を歌いました。僕は驚きました。その歌は僕を幸せな気分にさせました。",
        learning: {
          vocab: [
            { word: "bloom", mean: "咲く" },
            { word: "quiet", mean: "静かな" },
            { word: "surprised", mean: "驚いた" },
          ],
          grammar: {
            title: "make + O + C",
            desc: "「OをC（の状態）にさせる」という使役表現です。",
            key: "made me feel happy",
          },
        },
        color: "bg-yellow-100",
        accent: "text-yellow-600",
        mood: { rate: 1.0, pitch: 1.1 },
        image: "images/sample/02.jpg",
        startTime: 20,
      },
      // Part 3 removed for brevity
    ],
    quizzes: [
      {
        q: "Where did the boy find the seed?",
        options: ["In the park", "In the school", "In the river"],
        a: 0,
        exp: "Part 1で「I found a blue seed in the park.」と述べています。",
      },
      {
        q: "What color was the seed?",
        options: ["Red", "Blue", "Gold"],
        a: 1,
        exp: "Part 1に「blue seed」とあります。",
      },
      {
        q: "Who said it was a magic seed?",
        options: ["His mother", "His teacher", "His grandfather"],
        a: 2,
        exp: "Part 1で「My grandfather said...」とあります。",
      },
    ],
    puzzles: [
      {
        q: "公園で青い種を見つけました。",
        w: ["I", "found", "a", "blue", "seed", "in", "the", "park"],
        o: [0, 1, 2, 3, 4, 5, 6, 7],
      },
      {
        q: "それは星のように輝いていました。",
        w: ["It", "was", "shining", "like", "a", "star"],
        o: [0, 1, 2, 3, 4, 5],
      },
    ],
    fills: [
      {
        q: "I found a seed ------- the park.",
        options: ["on", "at", "in", "of"],
        a: 2,
        exp: "公園「の中で」なので in が適切です。",
      },
      {
        q: "It was shining ------- a star.",
        options: ["as", "like", "for", "to"],
        a: 1,
        exp: "「～のように」は like を使います。",
      },
    ],
  },
];

// Utility: Shuffle Array
const shuffleArray = (array) => {
  if (!array) return [];
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
};

// ==============================================================================
// 【SYSTEM SECTION】
// ==============================================================================

import { BrowserRouter, Routes, Route } from "react-router-dom"; // 追加

export default function App() {
  return (
    // 本番環境でClerk認証を有効にするため、ClerkProviderでラップしています。
    // 上記【STEP 3】の指示に従ってダミーコンポーネントを削除すれば、正しく機能します。

    <ClerkProvider publishableKey={clerkPubKey}>
      <BrowserRouter>
        <Routes>
          {/* 通常のサインイン画面を独立したパスで定義  */}
          <Route path="/sign-in/*" element={<SignIn routing="path" path="/sign-in" />} />
          <Route path="/sign-up/*" element={<SignUp routing="path" path="/sign-up" />} />
          
          {/* それ以外のすべてのパス（/ や /story/001 など）でメインコンテンツを表示 */}
          <Route path="*" element={<MainContent />} />
        </Routes>
      </BrowserRouter>
    </ClerkProvider>
        
    
    //<ClerkProvider publishableKey={clerkPubKey}>
    //  <MainContent />
    //</ClerkProvider>
  );
}

function MainContent() {
  // Clerk Hook (プレビュー環境ではダミーが動作)
  const { isSignedIn, userId: clerkUserId, getToken } = useAuth();
  const { user } = useUser();

  // ----------------------------------------------------------------------------
  // 1. URLパラメータから物語IDを取得 & サンプル判定
  // ----------------------------------------------------------------------------
  const searchParams = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
  const storyIdParam = searchParams.get("story");
  const isSampleStory = !storyIdParam || storyIdParam === "sample";
  
  // TTS Ref
  const currentUtteranceRef = useRef(null);
  const audioLockRef = useRef(false);
  // ★追加: クラウド音声再生用オーディオ
  const cloudAudioRef = useRef(new Audio());

  // Rate Limit & User Status Display
  // plan: 'guest' | 'free' | 'premium' | 'unknown'
  const [rateLimitInfo, setRateLimitInfo] = useState({
    plan: "unknown",
    remaining: null, // Speaking残回数
    limit: null, // Speaking上限
  });

  // ★追加: TTS制限情報 (仕様に合わせて追加)
  // 初期値をLocalStorageから復元するロジックに変更
  const [ttsStatus, setTtsStatus] = useState(() => {
    try {
      const saved = localStorage.getItem("er100_tts_status");
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed;
      }
    } catch (e) {
      console.error(e);
    }
    return {
      remaining: 10, // Freeプラン用初期値
      limit: 10,
      isUnlimited: false,
    };
  });

  // ttsStatusが変更されたら保存
  useEffect(() => {
    localStorage.setItem("er100_tts_status", JSON.stringify(ttsStatus));
  }, [ttsStatus]);

  // ユーザーID管理 (未登録ユーザー用)
  const [localUserId, setLocalUserId] = useState("");

  const generateUUID = () => {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
      /[xy]/g,
      function (c) {
        var r = (Math.random() * 16) | 0,
          v = c == "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      }
    );
  };

  useEffect(() => {
    let storedId = localStorage.getItem("er100_user_id");
    if (!storedId) {
      storedId = generateUUID();
      localStorage.setItem("er100_user_id", storedId);
    }
    setLocalUserId(storedId);
  }, []);

  const currentUserId = isSignedIn && clerkUserId ? clerkUserId : localUserId;

  // API Mode State
  // 'none' (No API), 'proxy' (Standard), 'custom' (Custom Key)
  const [apiMode, setApiMode] = useState(() => {
    // ★未ログインのサンプル時のみ強制的に 'none' から開始する
    if (!isSignedIn && isSampleStory) return "none";
    return localStorage.getItem("er100_api_mode") || "proxy";
  });

  // ★追加: closure対策用のRef
  const apiModeRef = useRef(apiMode);

  // Save to local storage when changed
  useEffect(() => {
    localStorage.setItem("er100_api_mode", apiMode);
    apiModeRef.current = apiMode;
  }, [apiMode]);

  // ★追加: Speaking制限回数超過時の自動切替
  useEffect(() => {
    if (
      apiMode === "proxy" &&
      rateLimitInfo.plan !== "premium" &&
      rateLimitInfo.remaining !== null &&
      rateLimitInfo.remaining <= 0
    ) {
      setApiMode("none");
      apiModeRef.current = "none";
    }
  }, [rateLimitInfo.remaining, rateLimitInfo.plan, apiMode]);

  const [customKey, setCustomKey] = useState(() => {
    return localStorage.getItem("gemini_local_key") || "";
  });

  const [customModel, setCustomModel] = useState(() => {
    return (
      localStorage.getItem("gemini_custom_model") || "gemini-2.5-flash-lite"
    );
  });

  // Voice States
  const [voices, setVoices] = useState([]);
  const [voiceIndex, setVoiceIndex] = useState(0);
  const [rate, setRate] = useState(0.9);
  const [showSettings, setShowSettings] = useState(false);
  
  // ★変更: Cloud Voiceの選択肢リスト (Excelデータより更新・顔アイコン削除・英語ラベル・性別付き)
  const CLOUD_VOICES = [
    { id: 1, label: "Elegant Narrator (Female)", gender: "Female", usId: "Studio-O", ukId: "Studio-C" },
    { id: 2, label: "Gentle Storyteller (Male)", gender: "Male", usId: "Studio-Q", ukId: "Studio-B" },
    { id: 3, label: "Warm Motherly (Female)", gender: "Female", usId: "Neural2-F", ukId: "Neural2-C" },
    { id: 4, label: "Cheerful Brother (Male)", gender: "Male", usId: "Neural2-J", ukId: "Neural2-B" },
    { id: 5, label: "Deep & Dignified (Male)", gender: "Male", usId: "Neural2-D", ukId: "Neural2-D" },
    { id: 6, label: "Brisk & Active (Female)", gender: "Female", usId: "Neural2-C", ukId: "Neural2-A" },
    { id: 7, label: "Crystal Clear (Female)", gender: "Female", usId: "Neural2-H", ukId: "Neural2-F" },
    { id: 8, label: "Kind & Mature (Female)", gender: "Female", usId: "Wavenet-F", ukId: "Wavenet-C" },
    { id: 9, label: "Curious Boy (Male)", gender: "Male", usId: "Neural2-I", ukId: "Wavenet-D" },
    { id: 10, label: "Smart & Cool (Female)", gender: "Female", usId: "Neural2-G", ukId: "Wavenet-F" },
  ];

  // Cloud Voice設定
  const [cloudVoiceIndex, setCloudVoiceIndex] = useState(() => {
    const saved = localStorage.getItem("er100_cloud_voice_index");
    return saved ? parseInt(saved, 10) : 0;
  });
  
  const [cloudRegion, setCloudRegion] = useState(() => {
    return localStorage.getItem("er100_cloud_region") || "US";
  });

  useEffect(() => {
    localStorage.setItem("er100_cloud_voice_index", cloudVoiceIndex);
    localStorage.setItem("er100_cloud_region", cloudRegion);
  }, [cloudVoiceIndex, cloudRegion]);

  // ★変更: 選択中の音声タイプ (browser / cloud)
  const [preferredVoiceType, setPreferredVoiceType] = useState("browser");

// ★追加: サインイン/アウトに伴うデフォルト値の自動切替と制限時の強制切替
  const prevPlanRef = useRef(rateLimitInfo.plan);
  useEffect(() => {
    // 1. プラン変更時（サインイン/アウト時）のデフォルト値設定
    if (prevPlanRef.current !== rateLimitInfo.plan && rateLimitInfo.plan !== "unknown") {
      if (rateLimitInfo.plan === "free" || rateLimitInfo.plan === "premium") {
        // サインイン時：標準モード(proxy)とCloud AI(cloud)をデフォルトに設定
        setApiMode("proxy");
        // TTS残回数がある、もしくはPremiumならCloud AIに設定
        if (ttsStatus.remaining > 0 || rateLimitInfo.plan === "premium") {
          setPreferredVoiceType("cloud");
        }
      } else if (rateLimitInfo.plan === "guest") {
        // サインアウト時：APIなし(none)とBrowser(browser)にリセット
        setApiMode("none");
        setPreferredVoiceType("browser");
      }
      prevPlanRef.current = rateLimitInfo.plan;
    }

    // 2. クォータ制限による強制切替（不整合防止：Premium以外で回数切れならBrowserへ戻す）
    if (rateLimitInfo.plan !== "premium") {
      if (ttsStatus.remaining <= 0 && preferredVoiceType === "cloud") {
        setPreferredVoiceType("browser");
      }
    }
  }, [rateLimitInfo.plan, ttsStatus.remaining, preferredVoiceType]);
  
  const [activeVoiceType, setActiveVoiceType] = useState("browser"); // 実際に再生中のタイプ
  
  // ★追加: 音声読み込み中フラグ
  const [isTTSLoading, setIsTTSLoading] = useState(false);

  // マウント状態を追跡するRef
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // ★ステータス取得関数（再利用可能に定義）
  const fetchStatus = useCallback(async () => {
    // Speaking Limit表示を常に行うため、apiModeに関わらずフェッチを試みる
    
    // プレビューモード（クライアントサイドAI）の場合のシミュレーション
    if (false) {
      if (isMountedRef.current) {
        setRateLimitInfo((prev) => {
          // 簡易シミュレーション
          const simulatedPlan = isSignedIn ? "free" : "guest";
          // ★修正: 仕様に合わせて修正 (Guest 5回, Free 10回)
          const simulatedLimit = simulatedPlan === "free" ? 10 : 5;

          // FIX: 状態が変わらない場合は更新しない（無限ループ防止）
          if (
            prev.plan === simulatedPlan &&
            prev.remaining === simulatedLimit &&
            prev.limit === simulatedLimit
          ) {
            return prev;
          }

          return {
            plan: simulatedPlan,
            remaining: simulatedLimit,
            limit: simulatedLimit,
          };
        });
      }
    }

    // =================================================================================
    // 🔴 本番作業: 以下の API呼び出しブロック全体 のコメントアウト「/* */」を外して有効化してください。
    // =================================================================================
    
    try {
      let token = "";
      if (isSignedIn) {
        token = await getToken();
      }

      const headers = {
        "Content-Type": "application/json",
      };

      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      let storedId = localStorage.getItem("er100_user_id");
      if (storedId) {
        headers["X-App-User-ID"] = storedId;
      }

      // 【修正】キャッシュを回避するためにタイムスタンプを付与
      const res = await fetch(`/api/gemini-speaking?t=${Date.now()}`, {
        method: "GET",
        headers: headers,
        cache: "no-store", // 【修正】明示的にキャッシュなしを指定
      });

      if (res.ok && isMountedRef.current) {
        const data = await res.json();

        // ★修正: サーバーがGuest判定しても、クライアント側でログイン中ならそのプラン情報を優先して表示する
        let displayPlan = data.plan;
        let displayLimit = data.limit;
        let displayRemaining = data.remaining;

        if (isSignedIn && user && data.plan === "guest") {
          // サーバー認証が失敗している場合でも、UI上はログイン状態に合わせて表示
          displayPlan =
            user.publicMetadata?.plan === "premium" ? "premium" : "free";
          // Limitもプランに合わせて上書き (Free: 30, Premium: 200)
          displayLimit = displayPlan === "premium" ? 200 : 30;

          // ★追加修正: 使用回数(Guestカウント)に基づいて残回数を再計算
          // Guestとしてカウントされた使用回数 = サーバー上のGuestLimit(10) - サーバー上のGuestRemaining
          const usage = data.limit - data.remaining;
          // 新しい残回数 = 新しいLimit - 使用回数 (0未満にはしない)
          displayRemaining = Math.max(0, displayLimit - usage);
        }

        setRateLimitInfo({
          plan: displayPlan,
          remaining: displayRemaining,
          limit: displayLimit,
        });

        // ★修正ポイント: サーバーから取得したTTS残り回数を正しく反映する
        setTtsStatus((prev) => ({
          ...prev,
          isUnlimited: displayPlan === "premium",
          limit: data.ttsLimit || (displayPlan === "free" ? 10 : 0),
          remaining: data.ttsRemaining !== undefined ? data.ttsRemaining : prev.remaining,
        }));
      } else {
        console.warn("Status fetch failed:", res.status);
      }
    } catch (e) {
      console.error("Failed to fetch status:", e);
    }
    
    // =================================================================================
  }, [apiMode, isSignedIn, getToken, user]);

  // ★初回起動時および依存変更時に取得
  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // ★設定画面が開かれたときに最新情報を再取得
  useEffect(() => {
    if (showSettings) {
      fetchStatus();
    }
  }, [showSettings, fetchStatus]);

  // ----------------------------------------------------------------------------
  // 2. データの取得
  // ----------------------------------------------------------------------------
  let storyObj = null;
  let targetId = "Sample";

  if (storyIdParam && storyIdParam !== "sample") {
    console.log("Using External Story Source for ID:", storyIdParam);
    try {
      if (typeof getStoryById === "function") {
        storyObj = getStoryById(storyIdParam);
      }
    } catch (e) {
      console.error("Failed to load external story:", e);
    }
    targetId = storyIdParam;
  }

  const loadedStory = storyObj || STORY_DATABASE[0];

  // Stateの初期化
  const [story, setStory] = useState(loadedStory.content || []);

  const getSavedState = (key, defaultValue) => {
    try {
      const saved = localStorage.getItem("er100_state_v2");
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed[key] !== undefined) {
          return parsed[key];
        }
      }
    } catch (e) {
      console.error("Failed to load saved state for key:", key, e);
    }
    return defaultValue;
  };

  // UI States
  const [page, setPage] = useState(() => getSavedState("page", 0));
  const [showRuby, setShowRuby] = useState(false);
  const [showSlash, setShowSlash] = useState(false);
  const [showTrans, setShowTrans] = useState(false);
  const [showGrammar, setShowGrammar] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [showMenu, setShowMenu] = useState(false); // サイドメニューの表示制御

  // 音声録音用のRef
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const mimeTypeRef = useRef("audio/webm");
  const recordingStartTimeRef = useRef(0);
  const isSimulationRef = useRef(false); // シミュレーションモード判定用

  // Settings for Question Counts
  const [quizCount, setQuizCount] = useState(5);
  const [scrambleCount, setScrambleCount] = useState(5);
  const [fillCount, setFillCount] = useState(5);
  const [speakingCount, setSpeakingCount] = useState(5);

  // Settings for Quiz Mode (New)
  const [quizDefaultMode, setQuizDefaultMode] = useState(
    () => localStorage.getItem("er100_quiz_mode") || "voice"
  );
  // クイズ画面での表示状態（初期値は設定に従う）
  const [isQuestionTextVisible, setIsQuestionTextVisible] = useState(
    quizDefaultMode === "text"
  );

  const changeQuizDefaultMode = (val) => {
    setQuizDefaultMode(val);
    localStorage.setItem("er100_quiz_mode", val);
  };

  const recognitionRef = useRef(null);
  const [localTranscript, setLocalTranscript] = useState("");
  // ★追加: closure対策用のRef
  const localTranscriptRef = useRef("");

  const handleCustomKeyChange = (e) => {
    const val = e.target.value;
    setCustomKey(val);
    localStorage.setItem("gemini_local_key", val);
  };

  const handleCustomModelChange = (e) => {
    const val = e.target.value;
    setCustomModel(val);
    localStorage.setItem("gemini_custom_model", val);
  };

  const [mode, setMode] = useState(() => getSavedState("mode", "reading"));
  const [previousMode, setPreviousMode] = useState("reading"); // 追加: 直前のモード保存用

  const [currentQuestions, setCurrentQuestions] = useState(() =>
    getSavedState("currentQuestions", [])
  );
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(() =>
    getSavedState("currentQuestionIndex", 0)
  );
  const [currentScore, setCurrentScore] = useState(() =>
    getSavedState("currentScore", 0)
  );
  const [currentTotal, setCurrentTotal] = useState(() =>
    getSavedState("currentTotal", 5)
  );

  const [selectedOption, setSelectedOption] = useState(null);
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [isCorrect, setIsCorrect] = useState(null);

  const [currentOrder, setCurrentOrder] = useState([]);
  const [scrambleResult, setScrambleResult] = useState(null);

  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [speakingResult, setSpeakingResult] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const audioRef = useRef(null);

  useEffect(() => {
    const loadVoices = () => {
      const allVoices = window.speechSynthesis.getVoices();
      const enVoices = allVoices.filter((v) => v.lang.startsWith("en"));
      setVoices(enVoices.length > 0 ? enVoices : allVoices);
      if (enVoices.length > 0) setVoiceIndex(0);
    };
    loadVoices();
    window.speechSynthesis.onvoiceschanged = loadVoices;
  }, []);

  useEffect(() => {
    if (mode === "reading") {
      setShowTrans(false);
      setShowGrammar(false);

      if (window.speechSynthesis.speaking || window.speechSynthesis.pending) {
        window.speechSynthesis.cancel();
      }
      setIsPlaying(false);
      setIsPaused(false);
      setIsTTSLoading(false); // 読み込み状態もリセット
    }
  }, [page, mode]);

  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    setIsInitialized(true);
  }, []);

  useEffect(() => {
    if (!isInitialized) return;

    const stateToSave = {
      page,
      mode,
      currentQuestions,
      currentQuestionIndex,
      currentScore,
      currentTotal,
    };
    const jsonState = JSON.stringify(stateToSave);
    const STORAGE_KEY = "er100_state_v2";
    localStorage.setItem(STORAGE_KEY, jsonState);
    sessionStorage.setItem(STORAGE_KEY, jsonState);
  }, [
    page,
    mode,
    currentQuestions,
    currentQuestionIndex,
    currentScore,
    currentTotal,
    isInitialized,
  ]);

  // ★Voice切り替え関数（ブラウザ用）
  const changeBrowserVoice = (direction) => {
    if (voices.length === 0) return;
    if (direction === "next") {
      setVoiceIndex((prev) => (prev + 1) % voices.length);
    } else {
      setVoiceIndex((prev) => (prev - 1 + voices.length) % voices.length);
    }
  };

  // ★Voice切り替え関数（クラウド用）
  const changeCloudVoice = (direction) => {
    if (CLOUD_VOICES.length === 0) return;
    let newIndex = 0;
    if (direction === "next") {
      newIndex = (cloudVoiceIndex + 1) % CLOUD_VOICES.length;
    } else {
      newIndex =
        (cloudVoiceIndex - 1 + CLOUD_VOICES.length) % CLOUD_VOICES.length;
    }
    setCloudVoiceIndex(newIndex);
  };

  if (!loadedStory.content || loadedStory.content.length === 0) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-slate-500 font-bold p-8">
        <p className="mb-4 text-xl">Story Not Found (ID: {targetId})</p>
      </div>
    );
  }

  const currentPageData = story[page];

  // エラー回避: ページ番号が物語の範囲を超えている場合、リセットして待機画面を表示
  if (!currentPageData) {
    if (story.length > 0 && page >= story.length) {
      setPage(0); // 強制的に1ページ目に戻す
    }
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500">
        Loading or Resetting State...
      </div>
    );
  }

  // ★ブラウザ音声再生（汎用化: 引数でテキストを指定可能に）
  // targetTextが指定されていない場合は現在のページの英文を再生
  const playBrowserTTS = (targetText = null) => {
    const synth = window.speechSynthesis;
    const currentVoices = voices.length > 0 ? voices : synth.getVoices();
    const textToSpeak = targetText || currentPageData.en;
    const utterance = new SpeechSynthesisUtterance(textToSpeak);
    utterance.lang = "en-US";

    if (currentVoices.length > 0) {
      const targetVoice = currentVoices[voiceIndex] || currentVoices[0];
      utterance.voice = targetVoice;
    }

    utterance.rate = rate;
    utterance.pitch = 1.0;

    utterance.onstart = () => {
      setIsPlaying(true);
      setIsPaused(false);
      setIsTTSLoading(false); // 読み込み完了
      audioLockRef.current = false;
      setActiveVoiceType("browser");
    };

    utterance.onend = () => {
      setIsPlaying(false);
      setIsPaused(false);
      audioLockRef.current = false;
    };

    utterance.onerror = (e) => {
      if (e.error === "interrupted" || e.error === "canceled") {
        setIsPlaying(false);
        setIsPaused(false);
        setIsTTSLoading(false);
        audioLockRef.current = false;
        return;
      }
      console.error("Browser TTS Error", e);
      setIsPlaying(false);
      setIsPaused(false);
      setIsTTSLoading(false);
      synth.cancel();
      audioLockRef.current = false;
    };

    currentUtteranceRef.current = utterance;
    synth.speak(utterance);
  };

  // ★クラウド音声再生（汎用化: 引数でテキストを指定可能に）
  const playCloudTTS = async (targetText = null) => {
    try {
      // 読み込み中フラグを立てる
      setIsTTSLoading(true);

      const textToSpeak = targetText || currentPageData.en;
      console.log(
        "Requesting Cloud TTS for:",
        textToSpeak.substring(0, 20) + "..."
      );

      // ★変更点: 物語のムード情報を取得
      const moodRate = currentPageData?.mood?.rate || 1.0;
      const moodPitch = currentPageData?.mood?.pitch
        ? (currentPageData.mood.pitch - 1.0) * 4
        : 0.0;

      // ★変更点: 選択されたキャラクターと言語からボイスIDを生成
      const currentVoiceData = CLOUD_VOICES[cloudVoiceIndex];
      const regionCode = cloudRegion === 'US' ? 'en-US' : 'en-GB';
      const voiceSuffix = cloudRegion === 'US' ? currentVoiceData.usId : currentVoiceData.ukId;
      const voiceName = `${regionCode}-${voiceSuffix}`;
      
      // =================================================================================
      // 🔴 本番作業: 以下の API呼び出しブロック全体 のコメントアウト「/* */」を外して有効化してください。
      // =================================================================================
      
      let token = "";
      try {
        if (isSignedIn) token = await getToken();
      } catch (e) {
        console.error(e);
      }

      const headers = {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      const res = await fetch("/api/tts", {
        method: "POST",
        headers: headers,
        body: JSON.stringify({
          text: textToSpeak,
          voiceName: voiceName, // 生成したボイスIDを送信
          speakingRate: rate, // ★修正: ユーザー設定の速度を使用
          pitch: moodPitch, // ピッチは物語のムードを使用
        }),
      });

      // ★【追加】429エラー(制限超過)の場合、即座にローカルの残り回数を0にしてブラウザ音声へ誘導
      if (res.status === 429) {
         console.warn("TTS Rate Limit Exceeded");
         setTtsStatus(prev => ({ ...prev, remaining: 0 })); // 強制的に0に更新
         throw new Error("Rate limit exceeded"); // catchブロックへ移動し、ブラウザ音声を再生
      }

      if (!res.ok) throw new Error("TTS API Error");
      const data = await res.json();

      // ★【修正・追加】サーバーからのレスポンスに基づいてTTS残り回数を更新
      // Freeプランの場合、クライアント側のカウントも減らすことで即座にUIへ反映させます
      if (data.remaining !== undefined) {
          setTtsStatus(prev => ({
              ...prev,
              remaining: data.remaining
          }));
      }

      // Base64から音声再生
      const audioSrc = `data:audio/mp3;base64,${data.audioContent}`;
      cloudAudioRef.current.src = audioSrc;
      
      // 再生開始
      await cloudAudioRef.current.play();

      // 再生状態管理
      setActiveVoiceType("cloud");
      setIsPlaying(true);
      setIsTTSLoading(false); // 読み込み完了

      cloudAudioRef.current.onended = () => {
        setIsPlaying(false);
        audioLockRef.current = false;
      };

      return; // 成功したらここで終了
      
      // =================================================================================

      // =================================================================================
      // 🔵 プレビュー環境(Canvas)用フォールバック
      // 本番環境に移行する際は、このブロック全体を削除またはコメントアウトしても良いですが、
      // APIエラー時のフォールバックとして残しておいても問題ありません。
      // =================================================================================
      console.log("【Preview Mode】Cloud TTS API is not available in Canvas. Falling back to Browser TTS.");
      
      // CanvasプレビューではAPIが動作しないため、ブラウザ音声で代用します。
      playBrowserTTS(targetText); 
      // playBrowserTTS内で setIsTTSLoading(false) が呼ばれます

    } catch (e) {
      console.error("Cloud TTS Error:", e);
      // エラー時もブラウザ音声へフォールバック
      playBrowserTTS(targetText);
      // playBrowserTTS内で setIsTTSLoading(false) が呼ばれます
    }
  };

  // ★スマートTTS再生（プランと残回数に応じた自動振り分け）
  const playSmartTTS = async (targetText = null) => {
    if (audioLockRef.current) return;
    
    // 再生中ならPause処理へ（handlePlayPause側で制御するが念の為）
    if (isPlaying) {
      handlePlayPause();
      return;
    }

    audioLockRef.current = true;
    window.speechSynthesis.cancel();
    cloudAudioRef.current.pause();

    // ★優先設定（preferredVoiceType）の確認
    // ユーザーが設定画面で「Browser Voice」を選んでいたら、強制的にブラウザ再生
    if (preferredVoiceType === "browser") {
      playBrowserTTS(targetText);
      setTimeout(() => {
        audioLockRef.current = false;
      }, 1000);
      return;
    }

    const plan = rateLimitInfo.plan;

    // Guest: 常にブラウザ音声 (Cloud設定は無効)
    if (plan === "guest") {
      playBrowserTTS(targetText);
    }
    // Premium: 常に高品質音声 (APIがある場合)
    else if (plan === "premium") {
      // 🔴 本番時はここが有効になる
      await playCloudTTS(targetText);
    }
    // Free: 回数制限内なら高品質、超過ならブラウザ
    else if (plan === "free") {
      if (ttsStatus.remaining > 0) {
        // 🔴 本番時はここが有効になる
        await playCloudTTS(targetText);
      } else {
        playBrowserTTS(targetText); // 上限到達後はブラウザ
      }
    }
    // 不明な場合
    else {
      playBrowserTTS(targetText);
    }

    // ロック解除の保険
    setTimeout(() => {
      audioLockRef.current = false;
    }, 1000);
  };

  // ★メインの再生ハンドラ（物語用）
  const handlePlayPause = async () => {
    if (audioLockRef.current) return;

    // 1. 停止(Pause)処理
    if (isPlaying) {
      window.speechSynthesis.pause();
      cloudAudioRef.current.pause(); // クラウド音声も停止
      setIsPlaying(false);
      setIsPaused(true);
      return;
    }

    // 2. 再開(Resume)処理
    if (isPaused) {
      // 直前のタイプに合わせて再開
      if (activeVoiceType === "browser") {
        window.speechSynthesis.resume();
      } else {
        cloudAudioRef.current.play();
      }
      setIsPlaying(true);
      setIsPaused(false);
      return;
    }

    // 3. 新規再生（Smart TTSを使用）
    // 引数なし = 現在のページの英文を再生
    await playSmartTTS();
  };

  const handleStop = () => {
    if (audioLockRef.current) return;
    audioLockRef.current = true;
    window.speechSynthesis.cancel();
    cloudAudioRef.current.pause(); // クラウド音声停止
    cloudAudioRef.current.currentTime = 0;
    setIsPlaying(false);
    setIsPaused(false);
    setIsTTSLoading(false); // 読み込み中であればキャンセル
    setTimeout(() => {
      audioLockRef.current = false;
    }, 500);
  };

  const playSampleAudio = () => {
    // Speakingモードのお手本音声もSmartTTSで再生
    if (currentQuestions[currentQuestionIndex]) {
      const text = currentQuestions[currentQuestionIndex].text;
      playSmartTTS(text);
    }
  };

  const playQuizQuestion = () => {
    // クイズの出題音声もSmartTTSで再生
    if (currentQuestions[currentQuestionIndex]) {
      const text = currentQuestions[currentQuestionIndex].q;
      playSmartTTS(text);
    }
  };

  const changeVoice = (index) => setVoiceIndex(index);
  const changeRate = (newRate) => setRate(newRate);

  const handlePageChange = (newPage) => {
    if (window.speechSynthesis.speaking || window.speechSynthesis.pending) {
      window.speechSynthesis.cancel();
    }
    setIsPlaying(false);
    setIsPaused(false);
    setIsTTSLoading(false);

    if (newPage >= 0 && newPage < story.length) {
      setPage(newPage);
    } else if (newPage === story.length) {
      setMode("menu");
    }
  };

  const startChallenge = (type) => {
    // 画面遷移を伴うので、ここでメニューを閉じる
    setShowMenu(false);

    let pool = [];
    let count = 5;

    if (type === "speaking") {
      const fullText = loadedStory.content.map((part) => part.en).join(" ");
      const sentences = fullText
        .replace(/([.!?]['"]?)\s+/g, "$1|")
        .split("|")
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
      pool = sentences
        .filter((s) => s.split(/\s+/).length >= 5)
        .map((s) => ({ text: s }));
      count = speakingCount;
    } else if (type === "quiz") {
      pool = loadedStory.quizzes || [];
      count = quizCount;
    } else if (type === "scramble") {
      pool = loadedStory.puzzles || [];
      count = scrambleCount;
    } else if (type === "fill") {
      pool = loadedStory.fills || [];
      count = fillCount;
    }

    if (pool.length === 0) {
      alert("No questions available for this mode.");
      return;
    }

    const shuffledPool = shuffleArray([...pool]);
    const actualCount = Math.min(count, pool.length);
    const selectedRaw = shuffledPool.slice(0, actualCount);

    const processedQuestions = selectedRaw.map((q) => {
      if (type === "quiz" || type === "fill") {
        const optionsWithFlag = q.options.map((opt, i) => ({
          text: opt,
          isCorrect: i === q.a,
        }));
        const shuffledOptions = shuffleArray(optionsWithFlag);
        const newAnswerIndex = shuffledOptions.findIndex((o) => o.isCorrect);
        return {
          ...q,
          options: shuffledOptions.map((o) => o.text),
          a: newAnswerIndex,
          originalExp: q.exp,
        };
      } else if (type === "scramble") {
        const indices = q.w.map((_, i) => i);
        const shuffledIndices = shuffleArray(indices);
        return {
          ...q,
          displayOrder: shuffledIndices,
        };
      } else {
        return q;
      }
    });

    setCurrentQuestions(processedQuestions);
    setCurrentQuestionIndex(0);
    setCurrentScore(0);
    setCurrentTotal(actualCount);
    setSelectedOption(null);
    setQuizSubmitted(false);
    setIsCorrect(null);
    setCurrentOrder([]);
    setScrambleResult(null);
    setIsRecording(false);
    setTranscript("");
    setSpeakingResult(null);
    setIsAnalyzing(false);
    setMode(type);

    // クイズ開始時の表示設定初期化 (デフォルトは音声ファースト=テキスト非表示)
    if (type === "quiz") {
      setIsQuestionTextVisible(false);
    }

    const stateToSave = {
      page,
      mode: type,
      currentQuestions: processedQuestions,
      currentQuestionIndex: 0,
      currentScore: 0,
      currentTotal: actualCount,
    };
    const jsonState = JSON.stringify(stateToSave);
    const STORAGE_KEY = "er100_state_v2";
    localStorage.setItem(STORAGE_KEY, jsonState);
    sessionStorage.setItem(STORAGE_KEY, jsonState);
  };

  // ★追加: Speaking練習の文章を更新する関数
  const refreshCurrentSpeakingQuestion = () => {
    const fullText = loadedStory.content.map((part) => part.en).join(" ");
    const sentences = fullText
      .replace(/([.!?]['"]?)\s+/g, "$1|")
      .split("|")
      .map((s) => s.trim())
      .filter((s) => s.length > 0 && s.split(/\s+/).length >= 5);
    
    if (sentences.length <= 1) return;

    // 現在とは別の文章を選択
    let nextSentence = sentences[Math.floor(Math.random() * sentences.length)];
    while(nextSentence === currentQuestions[currentQuestionIndex].text && sentences.length > 1) {
       nextSentence = sentences[Math.floor(Math.random() * sentences.length)];
    }

    const newQuestions = [...currentQuestions];
    newQuestions[currentQuestionIndex] = { text: nextSentence };
    setCurrentQuestions(newQuestions);
    
    // 状態のリセット
    setSpeakingResult(null);
    setTranscript("");
    setIsRecording(false);
  };

  const handleOptionSelect = (idx) => {
    if (quizSubmitted) return;
    setSelectedOption(idx);
  };

  const checkQuizAnswer = () => {
    if (selectedOption === null) return;
    const currentQ = currentQuestions[currentQuestionIndex];
    const correct = selectedOption === currentQ.a;
    setIsCorrect(correct);
    if (correct) setCurrentScore(currentScore + 1);
    setQuizSubmitted(true);
    // 答え合わせ時にテキストを表示
    setIsQuestionTextVisible(true);
  };

  const nextQuestion = () => {
    if (currentQuestionIndex < currentQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setSelectedOption(null);
      setQuizSubmitted(false);
      setIsCorrect(null);
      setCurrentOrder([]);
      setScrambleResult(null);
      setIsRecording(false);
      setTranscript("");
      setSpeakingResult(null);
      setIsAnalyzing(false);

      // 次の問題へ行くときに表示設定をリセット (常にテキスト非表示から開始)
      if (mode === "quiz") {
        setIsQuestionTextVisible(false);
      }
    } else {
      setMode("result");
    }
  };

  const handleWordClick = (wordIndex) => {
    if (currentOrder.includes(wordIndex)) {
      setCurrentOrder(currentOrder.filter((i) => i !== wordIndex));
    } else {
      setCurrentOrder([...currentOrder, wordIndex]);
    }
  };

  const checkScramble = () => {
    const currentQ = currentQuestions[currentQuestionIndex];
    if (currentOrder.length !== currentQ.w.length) return;
    const isMatch = currentOrder.every((val, i) => val === currentQ.o[i]);
    setScrambleResult(isMatch);
    if (isMatch) setCurrentScore(currentScore + 1);
  };

  const resetScrambleCurrent = () => {
    setCurrentOrder([]);
    setScrambleResult(null);
  };

  const nextScramble = () => {
    if (currentQuestionIndex < currentQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setCurrentOrder([]);
      setScrambleResult(null);
    } else {
      setMode("result");
    }
  };

  const startRecording = async () => {
    // 【修正点】: マイク権限取得前に、現在の状態をLocalStorageへ確実に保存します。
    // これにより、権限ダイアログでリロードが発生しても元の画面に戻れるようにします。
    if (currentQuestions.length > 0) {
      const stateToSave = {
        page,
        mode,
        currentQuestions,
        currentQuestionIndex,
        currentScore,
        currentTotal,
      };
      const jsonState = JSON.stringify(stateToSave);
      const STORAGE_KEY = "er100_state_v2";
      localStorage.setItem(STORAGE_KEY, jsonState);
      sessionStorage.setItem(STORAGE_KEY, jsonState);
    }

    // 1. マイク権限チェック
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      if (
        confirm(
          "お使いの環境（スマホプレビュー等）ではマイクアクセスがブロックされています。\nテスト用にマイク無しで動作確認しますか？（シミュレーションモード）"
        )
      ) {
        startSimulationMode();
        return;
      }
      alert(
        "録音機能を利用できません。(HTTPS接続、または許可された環境が必要です)"
      );
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      isSimulationRef.current = false; // 本物の録音であることをマーク

      let mediaRecorder;
      try {
        // オプションなしで初期化（これが最も安全）
        mediaRecorder = new MediaRecorder(stream);
      } catch (e) {
        console.warn("Default MediaRecorder failed, trying with options", e);
        try {
          const options = { mimeType: "audio/mp4" };
          mediaRecorder = new MediaRecorder(stream, options);
        } catch (e2) {
          // ここで失敗したらシミュレーションモードを提案
          if (
            confirm(
              "録音機能の初期化に失敗しました。シミュレーションモードで動作確認しますか？"
            )
          ) {
            startSimulationMode();
            return;
          }
          return;
        }
      }

      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };

      try {
        mediaRecorder.start();
        setIsRecording(true);
        setSpeakingResult(null);
        setTranscript("");
        setLocalTranscript("");
        localTranscriptRef.current = ""; // Refをリセット
        recordingStartTimeRef.current = Date.now();
      } catch (e) {
        console.error("Recorder start error:", e);
        alert("録音の開始に失敗しました: " + e.message);
      }

      // 音声認識（Web Speech API）の併用
      const SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SpeechRecognition) {
        const recognition = new SpeechRecognition();
        recognition.lang = "en-US";
        recognition.continuous = true;
        recognition.interimResults = false;

        recognition.onresult = (event) => {
          let text = "";
          for (let i = 0; i < event.results.length; i++) {
            text += event.results[i][0].transcript;
          }
          setLocalTranscript(text);
          localTranscriptRef.current = text; // Refを更新
        };

        try {
          recognition.start();
          recognitionRef.current = recognition;
        } catch (e) {
          console.warn("Speech recognition start failed", e);
        }
      }
    } catch (err) {
      console.error("Microphone error:", err);
      // エラー発生時はシミュレーションモードを提案
      if (
        confirm(
          "マイクへのアクセスが拒否されたか、エラーが発生しました。\nテスト用にマイク無しで動作確認しますか？（シミュレーションモード）"
        )
      ) {
        startSimulationMode();
      }
    }
  };

  // ★シミュレーションモード開始関数
  const startSimulationMode = () => {
    isSimulationRef.current = true;
    setIsRecording(true);
    setSpeakingResult(null);
    setTranscript("(Simulation Mode: Speaking...)");
    setLocalTranscript("This is a simulation.");
    localTranscriptRef.current = "This is a simulation.";
    recordingStartTimeRef.current = Date.now();

    // ダミーのMediaRecorderっぽいオブジェクトを作っておくとstop時にエラーにならない
    mediaRecorderRef.current = {
      state: "recording",
      stop: () => {},
      stream: { getTracks: () => [] },
    };
  };

  const blobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result.split(",")[1]);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const stopRecording = () => {
    // シミュレーションモードの場合の処理
    if (isSimulationRef.current) {
      setIsRecording(false);
      setSpeakingResult({
        score: 0,
        feedback: "これはシミュレーションモードです。",
        advice:
          "プレビュー環境でマイクが使えないため、通信テストを行いました。\n本番環境では実際のマイク音声が使用されます。",
        transcription: "This is a simulation test.",
      });
      return;
    }

    // 以下, 通常のマイク録音停止処理
    if (mediaRecorderRef.current && isRecording) {
      const recordingDuration = Date.now() - recordingStartTimeRef.current;
      if (recordingDuration < 1000) {
        if (mediaRecorderRef.current.state !== "inactive") {
          mediaRecorderRef.current.stop();
        }
        if (recognitionRef.current) recognitionRef.current.stop();

        setIsRecording(false);
        setSpeakingResult({
          score: 0,
          feedback: "録音時間が短すぎます。",
          advice: "ボタンをタップして、もう少し長く話してください。",
        });
        return;
      }

      if (mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
      setIsRecording(false);

      mediaRecorderRef.current.onstop = async () => {
        const type =
          mediaRecorderRef.current.mimeType ||
          mimeTypeRef.current ||
          "audio/webm";
        const audioBlob = new Blob(audioChunksRef.current, { type: type });

        if (mediaRecorderRef.current.stream) {
          mediaRecorderRef.current.stream
            .getTracks()
            .forEach((track) => track.stop());
        }

        if (recognitionRef.current) {
          recognitionRef.current.stop();
        }

        if (audioBlob.size < 3000) {
          setSpeakingResult({
            score: 0,
            feedback: "音声が短すぎます。",
            advice: "もう少し長く話してください。",
          });
          return;
        }

        try {
          const base64Audio = await blobToBase64(audioBlob);
          analyzeSpeaking(base64Audio);
        } catch (e) {
          runSimpleEvaluation("Audio processing failed: " + e.message);
        }
      };
    }
  };

  const runSimpleEvaluation = (errorMsg) => {
    console.log("Running fallback simple evaluation due to:", errorMsg);

    // ★Refを使用して最新の認識テキストを取得（closure対策）
    const spokenText = localTranscriptRef.current || "";
    const targetText = currentQuestions[currentQuestionIndex].text;

    const normalize = (str) =>
      str
        .toLowerCase()
        .replace(/[.,?!'"]/g, "")
        .split(/\s+/)
        .filter((w) => w);
    const targetWords = normalize(targetText);
    const spokenWords = normalize(spokenText);

    if (spokenWords.length === 0) {
      // ★修正: エラーメッセージ判定を改善
      const isApiKeyMissing = errorMsg && errorMsg.toString().includes("API_KEY_MISSING");
      setSpeakingResult({
        score: 0,
        feedback: isApiKeyMissing ? "音声認識エラー（AI接続不可・認識不可）" : "音声が聞き取れませんでした",
        advice: isApiKeyMissing 
          ? "現在、AIキーが設定されていません。\n設定メニュー(⚙️)から「Custom Key」を入力すると、AIによる詳細な発音診断が利用できます。\n※現在はブラウザ標準の音声認識で簡易評価を行いました。"
          : (errorMsg ? `サーバー接続エラーか、マイク音声がありません。\nError: ${errorMsg}` : "音声が検出されなかったか、認識できませんでした。マイクが音を拾っているか確認して、もう少しはっきり話してみてください。"),
        transcription: "(No speech detected)",
      });
      setIsAnalyzing(false);
      return;
    }

    let matchCount = 0;
    spokenWords.forEach((w) => {
      if (targetWords.includes(w)) matchCount++;
    });

    let ratio = matchCount / targetWords.length;
    if (ratio > 1) ratio = 1;

    let score = Math.floor(ratio * 5);
    if (score < 1 && ratio > 0) score = 1;

    let adviceText = "";
    const isApiKeyMissing = errorMsg && errorMsg.toString().includes("API_KEY_MISSING");
    if (isApiKeyMissing) {
      adviceText =
        "現在、AIキーが設定されていません。\n設定メニュー(⚙️)から「Custom Key」を入力すると、AIによる詳細な発音診断が利用できます。\n※現在はブラウザ標準の音声認識で簡易評価を行いました。";
    } else if (!errorMsg) {
       adviceText = "APIを使用しない簡易評価モードで判定しました。マイクに向かって話した単語の一致率でスコアを計算しています。";
    } else {
      adviceText = `サーバー接続エラーか、マイク音声がありません。\nError: ${errorMsg}`;
    }

    setSpeakingResult({
      score: score,
      feedback: "⚠️ 簡易評価モード",
      advice: adviceText,
      transcription: spokenText,
    });

    setTranscript(spokenText);
    setIsAnalyzing(false);

    if (score >= 3) {
      setCurrentScore((prev) => prev + 1);
    }
  };

  // ----------------------------------------------------------------------------
  // ▼▼▼ AI分析ロジック (ハイブリッド仕様) ▼▼▼
  // ----------------------------------------------------------------------------
  const analyzeSpeaking = async (base64Audio) => {
    // Mode 1: No API (Local Simple Evaluation)
    // ★Refを使用して最新のモードを判定
    if (apiModeRef.current === "none") {
      // ブラウザ認識結果が確定するまでわずかに待機
      await new Promise(resolve => setTimeout(resolve, 500));
      runSimpleEvaluation(null); // エラーではないのでnull
      return;
    }

    setIsAnalyzing(true);
    const targetText = currentQuestions[currentQuestionIndex].text;
    const simpleMimeType = (mimeTypeRef.current || "audio/webm").split(";")[0];

    try {
      let aiResult = null;

      // =================================================================================
      // 🔵 プレビュー用シミュレーション
      // =================================================================================
      /*
      if (true) {
        // キーがない場合はエラー
        const activeKey = customKey || "";
        // ★Refを使用して最新のモードで判定
        if (apiModeRef.current === "custom" && !activeKey)
          throw new Error("API_KEY_MISSING");
        if (apiModeRef.current === "proxy") throw new Error("API_KEY_MISSING");

        const model = customModel || "gemini-2.5-flash-lite";
        const prompt = `
            You are a professional, extremely strict English pronunciation evaluator.
            **Target Sentence**: "${targetText}"
            **Output Format (JSON only)**:
            {
              "score": (integer 0–5),
              "transcription": "(what you actually heard)",
              "feedback": "(brief Japanese summary)",
              "advice": "(300–350 characters Japanese advice)"
            }`;

        const geminiRes = await fetch(
          `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${activeKey}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              contents: [
                {
                  parts: [
                    { text: prompt },
                    {
                      inline_data: {
                        mime_type: simpleMimeType,
                        data: base64Audio,
                      },
                    },
                  ],
                },
              ],
            }),
          }
        );

        if (!geminiRes.ok) throw new Error("Gemini API Request Failed");
        const data = await geminiRes.json();
        const textResult = data.candidates?.[0]?.content?.parts?.[0]?.text;
        if (!textResult) throw new Error("No response text from AI");
        const jsonMatch = textResult.match(/\{[\s\S]*\}/);
        if (!jsonMatch) throw new Error("Invalid JSON format");
        aiResult = JSON.parse(jsonMatch[0]);
      }
      */
      // =================================================================================
      // 🔴 本番作業: 以下の API呼び出しブロック全体 のコメントアウト「/* */」を外して有効化してください。
      // =================================================================================
      
      let token = "";
      try {
        // ClerkのgetToken (ダミー環境では "dummy_preview_token" が返る)
        token = await getToken();
      } catch (e) {
        console.log("Guest User or Token Error", e);
      }

      // カスタム設定がある場合はヘッダーに乗せる
      const headers = {
        "Content-Type": "application/json",
        "X-App-User-ID": currentUserId,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      if (apiModeRef.current === "custom" && customKey) {
        headers["X-Custom-Gemini-Key"] = customKey;
        if (customModel) {
          headers["X-Custom-Model"] = customModel;
        }
      }

      // サーバーAPI呼び出し
      const response = await fetch("/api/gemini-speaking", {
        method: "POST",
        headers: headers,
        body: JSON.stringify({
          audio: base64Audio,
          mimeType: simpleMimeType,
          targetText: targetText,
        }),
      });

      // レート制限エラー (429) のハンドリング
      if (response.status === 429) {
        const errData = await response.json();
        throw new Error(errData.message || "利用制限回数を超えました。");
      }

if (!response.ok) {
        const errData = await response.json().catch(() => ({}));

        // --- 400エラー（門番の検知）を画面に反映させる ---
        if (response.status === 400 && errData.advice) {
          setSpeakingResult({
            score: errData.score || 0,
            feedback: errData.feedback || "音声が聞き取れませんでした",
            advice: errData.advice, // AIの「マイクを確認してください」を表示
            transcription: errData.transcription || "[SILENCE]"
          });
          setTranscript(errData.transcription || "[SILENCE]");
          setIsAnalyzing(false);
          return; // 処理を正常終了
        }

        throw new Error(errData.error || errData.message || `Server Error: ${response.status}`);
      }

      aiResult = await response.json();
      
      // =================================================================================

      // 共通: 結果の反映
      if (aiResult) {
        // 修正: 無音やノイズの場合, および判定テキストに「0点」が含まれる場合にスコアを確実に0にする
        const isFailure = 
          aiResult.transcription === "NO_SPEECH" || 
          aiResult.transcription === "[SILENCE]" || 
          aiResult.transcription === "[UNINTELLIGIBLE]" ||
          aiResult.score === 0 ||
          (aiResult.feedback && aiResult.feedback.includes("0点"));

        if (isFailure) {
          setSpeakingResult({
            score: 0,
            feedback:
              "音声が検出されませんでした。\nマイクに近づいて, もう一度はっきりお話しください。",
          });
          setTranscript("(音声が検出されませんでした)");
        } else {
          setSpeakingResult({
            score: aiResult.score,
            feedback: aiResult.feedback,
            advice: aiResult.advice,
          });
          setTranscript(aiResult.transcription || "");

          if (aiResult.score >= 3) {
            setCurrentScore((prev) => prev + 1);
          }
        }
      } else {
        throw new Error("No result");
      }
    } catch (e) {
      // エラー時は簡易音声認識でフォールバック
      const msg = e.message || e.toString();
      if (msg.includes("API_KEY_MISSING")) {
        console.log(
          "AI Key missing in preview mode. Falling back to simple evaluation."
        );
        runSimpleEvaluation("API_KEY_MISSING");
      } else {
        console.error("AI Analysis failed, falling back:", e);
        runSimpleEvaluation(msg);
      }
      return;
    }

    setIsAnalyzing(false);
  };

  const backToMenu = () => {
    setMode("menu");
    setShowMenu(false);
  };
  const backToStory = () => {
    setPage(0);
    setMode("reading");
    setShowMenu(false);
  };

  // ★ 追加: ヘルプ画面から戻る処理
  const backFromHelp = () => {
    setMode(previousMode);
    setShowMenu(false);
  };

  // テキスト内の **文字列** を太字にするヘルパー関数
  const formatText = (text) => {
    if (!text) return null;
    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, index) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return (
          <strong key={index} className="text-slate-800 font-black">
            {part.slice(2, -2)}
          </strong>
        );
      }
      return part;
    });
  };

  const getResultContent = () => {
    const percentage = currentScore / currentTotal;
    if (percentage === 1)
      return {
        title: "Perfect!",
        msg: "全問正解！完璧です！",
        color: "text-amber-500",
      };
    if (percentage >= 0.8)
      return {
        title: "Great Job!",
        msg: "素晴らしい結果です！",
        color: "text-blue-500",
      };
    return {
      title: "Nice Try!",
      msg: "惜しい！もう一度挑戦してね。",
      color: "text-slate-500",
    };
  };

  const resultContent = mode === "result" ? getResultContent() : null;

  // Helper for rendering
  const isLastPage = page === story.length - 1;

  return (
    <div className="min-h-[100dvh] bg-slate-100 font-sans text-slate-800 flex justify-center py-0 sm:py-6">
      <div className="w-full max-w-lg bg-white shadow-2xl h-[100dvh] sm:min-h-[800px] sm:h-[90vh] sm:rounded-3xl flex flex-col relative overflow-hidden border border-slate-200">
        {/* Header */}
        <header className="bg-white/95 backdrop-blur-md border-b border-slate-100 p-4 sticky top-0 z-20 flex justify-between items-center h-16 shrink-0">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <button
              onClick={() => setShowMenu(true)}
              className="w-10 h-10 rounded-full border border-slate-100 bg-slate-50 flex items-center justify-center text-slate-500 hover:bg-slate-100 transition-colors flex-shrink-0"
            >
              <MenuIcon size={20} />
            </button>

            <div className="flex flex-col justify-center min-w-0 flex-1">
              <span className="text-[10px] font-bold text-amber-500 uppercase tracking-wider leading-tight mb-0.5">
                {loadedStory.level} / {loadedStory.category}
              </span>
              <h1 className="text-lg font-bold text-slate-800 leading-tight truncate w-full">
                {loadedStory.title}
              </h1>
            </div>
          </div>

          <div className="relative flex-shrink-0 ml-2">
            <div className="flex items-center gap-2">
              <SignedIn>
                <UserButton
                  afterSignOutUrl={
                    typeof window !== "undefined"
                      ? `${window.location.pathname}${window.location.search}`
                      : "/"
                  }
                />
              </SignedIn>
              <SignedOut>
                {/* ★サンプル時はClerkのSignInButtonを呼び出さず、グレーアウトしたダミーボタンを表示 */}
                {isSampleStory ? (
                  <button disabled className="px-4 py-2 bg-slate-300 text-slate-500 rounded-lg font-bold text-xs cursor-not-allowed">
                    Sign In
                  </button>
                ) : (
                  <SignInButton />
                )}
                </SignedOut>
            </div>
          </div>
        </header>

        {/* --- Side Menu Drawer --- */}
        {showMenu && (
          <div className="absolute inset-0 z-50 flex">
            <div
              className="absolute inset-0 bg-black/20 backdrop-blur-sm animate-in fade-in"
              onClick={() => setShowMenu(false)}
            ></div>
            <div className="relative w-64 bg-white h-full shadow-2xl p-6 flex flex-col animate-in slide-in-from-left duration-300">
              <div className="flex justify-between items-center mb-8 border-b border-slate-100 pb-4">
                <span className="text-sm font-black text-slate-800 uppercase tracking-widest">
                  Menu
                </span>
                <button
                  onClick={() => setShowMenu(false)}
                  className="p-2 -mr-2 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-50 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="space-y-2 flex-1 overflow-y-auto">
                <button
                  onClick={backToStory}
                  className={`w-full text-left px-4 py-3 rounded-xl font-bold flex items-center gap-3 transition-colors ${
                    mode === "reading"
                      ? "bg-amber-50 text-amber-600"
                      : "text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  <BookOpen size={18} />
                  <span>物語を読む</span>
                </button>

                <button
                  onClick={backToMenu}
                  className={`w-full text-left px-4 py-3 rounded-xl font-bold flex items-center gap-3 transition-colors ${
                    mode === "menu"
                      ? "bg-indigo-50 text-indigo-600"
                      : "text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  <ListChecks size={18} />
                  <span>練習メニュー</span>
                </button>

                <div className="pt-4 mt-4 border-t border-slate-100">
                  <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2 px-4">
                    Challenges
                  </div>
                  <button
                    onClick={() => startChallenge("quiz")}
                    className="w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-indigo-50 hover:text-indigo-600 flex items-center gap-3 transition-colors"
                  >
                    <ListChecks size={16} /> Quiz
                  </button>
                  <button
                    onClick={() => startChallenge("scramble")}
                    className="w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-emerald-50 hover:text-emerald-600 flex items-center gap-3 transition-colors"
                  >
                    <Puzzle size={16} /> Puzzle
                  </button>
                  <button
                    onClick={() => startChallenge("fill")}
                    className="w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-rose-50 hover:text-rose-600 flex items-center gap-3 transition-colors"
                  >
                    <PenTool size={16} /> Grammar
                  </button>
                  <button
                    onClick={() => startChallenge("speaking")}
                    className="w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-cyan-50 hover:text-cyan-600 flex items-center gap-3 transition-colors"
                  >
                    <Mic size={16} /> Speaking
                  </button>
                </div>
              </div>

              {/* Settings & Help Buttons */}
              <div className="pt-4 border-t border-slate-100 mt-2 space-y-2">
                <button
                  onClick={() => {
                    setShowMenu(false);
                    setShowSettings(true);
                  }}
                  className="w-full text-left px-4 py-3 rounded-xl font-bold flex items-center gap-3 text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition-colors"
                >
                  <Settings size={18} />
                  <span>Settings</span>
                </button>
                {/* Help Button with return memory */}
                <button
                  onClick={() => {
                    setShowMenu(false);
                    setPreviousMode(mode); // 現在のモードを記憶
                    setMode("help");
                  }}
                  className="w-full text-left px-4 py-3 rounded-xl font-bold flex items-center gap-3 text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition-colors"
                >
                  <HelpCircle size={18} />
                  <span>Help / 使い方</span>
                </button>
              </div>

              <div className="pt-4 mt-2 border-t border-slate-100 text-center">
                <p className="text-[10px] text-slate-400">
                  © 2025 <a href="https://ehon.firmcues.com" target="_blank">100 Storis Project</a>
                </p>
              </div>
            </div>
          </div>
        )}

        {/* --- Settings Modal --- */}
        {showSettings && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-in fade-in">
            <div className="w-full max-w-sm bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-slate-100 p-5 m-4 animate-in zoom-in-95 relative max-h-[90vh] overflow-y-auto">
              <button
                onClick={() => setShowSettings(false)}
                className="absolute top-4 right-4 p-1 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-100 transition-colors"
              >
                <X size={20} />
              </button>

              <div className="flex flex-col gap-4">
                <div className="text-lg font-black text-slate-800 mb-2 px-1">
                  Settings
                </div>
                {/* Account Status Section */}
                <div className="bg-slate-50 p-3 rounded-lg border border-slate-100">
                  <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                    <Activity size={12} /> Account Status
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-slate-500 font-bold">Plan</span>
                      <span
                        className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                          rateLimitInfo.plan === "premium"
                            ? "bg-amber-100 text-amber-700"
                            : rateLimitInfo.plan === "free"
                            ? "bg-blue-100 text-blue-700"
                            : "bg-slate-200 text-slate-600"
                        }`}
                      >
                        {rateLimitInfo.plan}
                      </span>
                    </div>

                    {/* TTS Limit Info (Updated based on spec) */}
                    <div className="flex justify-between items-center text-xs pt-1 border-t border-slate-200 mt-1">
                      <span className="text-slate-500 font-bold flex items-center gap-1">
                        <Volume2 size={10} /> Text-to-Speech
                      </span>
                      <span className="text-slate-700 font-mono">
                        {ttsStatus.isUnlimited ? (
                          <span className="text-green-600 font-bold">
                            Unlimited
                          </span>
                        ) : rateLimitInfo.plan === "guest" ? (
                          <span className="text-slate-400">Browser Only</span>
                        ) : (
                          <>
                            {ttsStatus.remaining}{" "}
                            <span className="text-slate-400">
                              / {ttsStatus.limit}
                            </span>
                          </>
                        )}
                      </span>
                    </div>

                    <div className="flex justify-between items-center text-xs">
                      <span className="text-slate-500 font-bold flex items-center gap-1">
                        <Mic size={10} /> Speaking Limit
                      </span>
                      <span className="text-slate-700 font-mono">
                        {apiMode === "custom" ? (
                          <span className="text-slate-400">Unlimited</span>
                        ) : (
                          <>
                            {rateLimitInfo.remaining !== null ? rateLimitInfo.remaining : 0}{" "}
                            <span className="text-slate-400">
                              / {rateLimitInfo.limit !== null ? rateLimitInfo.limit : (rateLimitInfo.plan === 'free' ? 10 : 5)}
                            </span>
                          </>
                        )}
                      </span>
                    </div>

                    {/* Clerk User Info (Dummy in preview) */}
                    {user && (
                      <div className="pt-2 border-t border-slate-200 mt-1">
                        <div className="text-[10px] text-slate-400">
                          Logged in as:
                        </div>
                        <div className="text-xs font-bold text-slate-700 truncate">
                          {user.fullName || user.username || "User"}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-2 border-b pb-2">
                  <Settings size={12} /> Detailed Settings
                </div>

                <div className="space-y-4">
                  {/* 音声タイプ切り替え (Premium/Free) */}
                  <div className="mb-4">
                    <div className="text-sm font-bold text-slate-700 mb-2">
                      Voice Type
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setPreferredVoiceType("browser")}
                        className={`flex-1 py-2 px-3 rounded-lg text-xs font-bold transition-all border ${
                          preferredVoiceType === "browser"
                            ? "bg-slate-800 text-white border-slate-800"
                            : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                        }`}
                      >
                        <WifiOff size={14} className="inline mr-1" /> Browser
                      </button>
                      <button
                        disabled={
                          rateLimitInfo.plan === "guest" ||
                          (rateLimitInfo.plan === "free" && ttsStatus.remaining <= 0)
                        }
                        onClick={() => setPreferredVoiceType("cloud")}
                        className={`flex-1 py-2 px-3 rounded-lg text-xs font-bold transition-all border ${
                          preferredVoiceType === "cloud"
                            ? "bg-amber-100 text-amber-700 border-amber-300"
                            : rateLimitInfo.plan === "guest" || (rateLimitInfo.plan === "free" && ttsStatus.remaining <= 0)
                              ? "bg-slate-100 text-slate-300 border-slate-200 cursor-not-allowed"
                              : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                        }`}
                      >
                        <Sparkles size={14} className="inline mr-1" /> Cloud
                        (AI)
                      </button>
                    </div>
                    {/* Freeプランで回数切れの場合のメッセージ */}
                    {rateLimitInfo.plan === "free" &&
                      ttsStatus.remaining <= 0 && (
                        <p className="text-[10px] text-red-500 mt-1 font-bold text-center">
                          ※制限回数を超えています。(Premiumで無制限)
                        </p>
                      )}
                    {/* Guestプランの場合のメッセージ */}
                    {rateLimitInfo.plan === "guest" && (
                      <p className="text-[10px] text-slate-400 mt-1 text-center">
                        ※AI音声はFree/Premiumプラン限定機能です。
                      </p>
                    )}
                  </div>

                  {/* Common Audio Settings */}
                  <div className="mb-4">
                    <div className="text-sm font-bold text-slate-700 mb-2 flex justify-between items-center">
                      <span>
                        {preferredVoiceType === "browser"
                          ? "Browser Voice"
                          : "AI Voice (Cloud)"}
                      </span>
                      {preferredVoiceType === "cloud" && (
                        <div className="flex bg-slate-200 rounded-lg p-0.5">
                          <button
                            onClick={() => setCloudRegion("US")}
                            className={`px-2 py-0.5 rounded text-[10px] font-bold transition-all ${
                              cloudRegion === "US"
                                ? "bg-white text-slate-800 shadow-sm"
                                : "text-slate-500 hover:text-slate-700"
                            }`}
                          >
                            US
                          </button>
                          <button
                            onClick={() => setCloudRegion("UK")}
                            className={`px-2 py-0.5 rounded text-[10px] font-bold transition-all ${
                              cloudRegion === "UK"
                                ? "bg-white text-slate-800 shadow-sm"
                                : "text-slate-500 hover:text-slate-700"
                            }`}
                          >
                            UK
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center justify-between bg-slate-100 rounded-lg p-1">
                      <button
                        onClick={() =>
                          preferredVoiceType === "browser"
                            ? changeBrowserVoice("prev")
                            : changeCloudVoice("prev")
                        }
                        className="p-1 hover:bg-white rounded-md transition-colors"
                      >
                        <ChevronLeft size={16} />
                      </button>

                      <div className="flex flex-col items-center">
                        {preferredVoiceType === "browser" ? (
                          <span className="text-xs font-mono text-slate-600 truncate max-w-[200px] block text-center">
                            {voices[voiceIndex]?.name.replace(
                              /^Microsoft |^Google | English/g,
                              ""
                            ) || "Default"}
                          </span>
                        ) : (
                          <>
                            <span className="text-xs font-bold text-amber-700 truncate max-w-[200px] block text-center">
                               {CLOUD_VOICES[cloudVoiceIndex].label}
                            </span>
                          </>
                        )}
                      </div>

                      <button
                        onClick={() =>
                          preferredVoiceType === "browser"
                            ? changeBrowserVoice("next")
                            : changeCloudVoice("next")
                        }
                        className="p-1 hover:bg-white rounded-md transition-colors"
                      >
                        <ChevronRight size={16} />
                      </button>
                    </div>

                    <div className="mt-1 text-center">
                      {preferredVoiceType === "browser" ? (
                        <span className="text-[10px] text-slate-400">
                          (Offline / No Usage Limit)
                        </span>
                      ) : (
                        <span className="text-[10px] text-amber-600">
                          (High Quality / Usage Limit applies)
                        </span>
                      )}
                    </div>
                  </div>

                  <div>
                    <div className="text-sm font-bold text-slate-700 mb-2">
                      Speed: {rate}x
                    </div>
                    <input
                      type="range"
                      min="0.5"
                      max="1.5"
                      step="0.1"
                      value={rate}
                      onChange={(e) => changeRate(parseFloat(e.target.value))}
                      className="w-full accent-amber-500 h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="flex justify-between text-[10px] text-slate-400 mt-1 font-mono">
                      <span>0.5</span>
                      <span>1.0</span>
                      <span>1.5</span>
                    </div>
                  </div>

                  {/* Challenge Settings */}
                  <div className="pt-2 border-t border-slate-200">
                    <div className="text-xs font-bold text-slate-500 mb-2">
                      Challenge Count
                    </div>
                    <div className="space-y-3">
                      <div>
                        <div className="flex justify-between text-sm font-bold text-slate-700 mb-1">
                          <span>Quiz</span>
                          <span>{quizCount}</span>
                        </div>
                        <input
                          type="range"
                          min="1"
                          max="15"
                          step="1"
                          value={quizCount}
                          onChange={(e) =>
                            setQuizCount(parseInt(e.target.value))
                          }
                          className="w-full accent-indigo-500 h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                        />
                      </div>
                      <div>
                        <div className="flex justify-between text-sm font-bold text-slate-700 mb-1">
                          <span>Puzzle</span>
                          <span>{scrambleCount}</span>
                        </div>
                        <input
                          type="range"
                          min="1"
                          max="15"
                          step="1"
                          value={scrambleCount}
                          onChange={(e) =>
                            setScrambleCount(parseInt(e.target.value))
                          }
                          className="w-full accent-emerald-500 h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                        />
                      </div>
                      <div>
                        <div className="flex justify-between text-sm font-bold text-slate-700 mb-1">
                          <span>Grammar</span>
                          <span>{fillCount}</span>
                        </div>
                        <input
                          type="range"
                          min="1"
                          max="15"
                          step="1"
                          value={fillCount}
                          onChange={(e) =>
                            setFillCount(parseInt(e.target.value))
                          }
                          className="w-full accent-rose-500 h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                        />
                      </div>
                      <div>
                        <div className="flex justify-between text-sm font-bold text-slate-700 mb-1">
                          <span>Speaking</span>
                          <span>{speakingCount}</span>
                        </div>
                        <input
                          type="range"
                          min="1"
                          max="15"
                          step="1"
                          value={speakingCount}
                          onChange={(e) =>
                            setSpeakingCount(parseInt(e.target.value))
                          }
                          className="w-full accent-cyan-500 h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                        />
                      </div>
                    </div>
                  </div>

                  {/* API Settings */}
                  <div className="border-t border-slate-200 pt-4 mt-2">
                    <div className="flex items-center gap-2 text-sm font-bold text-slate-700 mb-2">
                      <Key size={14} className="text-cyan-600" /> Speaking API
                      Mode
                    </div>
                    <div className="space-y-2 bg-slate-50 p-3 rounded-lg border border-slate-100">
                      {/* Option 1: No API */}
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="apiMode"
                          value="none"
                          checked={apiMode === "none"}
                          onChange={(e) => {
                            const val = e.target.value;
                            setApiMode(val);
                            apiModeRef.current = val;
                          }}
                          className="mt-0.5 accent-cyan-600"
                        />
                        <div>
                          <span className="block text-xs font-bold text-slate-700">
                            ① APIを使用しない (No AI)
                          </span>
                          <span className="block text-[10px] text-slate-400">
                            簡易発音判定（テキスト比較のみ）
                          </span>
                        </div>
                      </label>

                      {/* Option 2: Standard API (回数切れ時はグレーアウト) */}
                      <label className={`flex items-start gap-2 ${
                        // ★ 未ログインのサンプル時、または制限到達時に制限
                        (!isSignedIn && isSampleStory) || (rateLimitInfo.plan !== "premium" && rateLimitInfo.remaining !== null && rateLimitInfo.remaining <= 0) 
                        ? "opacity-50 cursor-not-allowed" 
                        : "cursor-pointer"
                      }`}>
                        <input
                          type="radio"
                          name="apiMode"
                          value="proxy"
                          checked={apiMode === "proxy"}
                          // ★ 未ログインのサンプル時、または制限到達時に制限
                          disabled={(!isSignedIn && isSampleStory) || (rateLimitInfo.plan !== "premium" && rateLimitInfo.remaining !== null && rateLimitInfo.remaining <= 0)}
                          onChange={(e) => {
                            const val = e.target.value;
                              setApiMode(val);
                              apiModeRef.current = val;
                          }}
                          className="mt-0.5 accent-cyan-600"
                        />

                        <div>
                          <span className="block text-xs font-bold text-slate-700">② 標準モード (Standard)</span>
                          <span className="block text-[10px] text-slate-400">
                            アプリ標準のAPI (Gemini 2.5 Flash Lite) を利用。
                            <br />
                            (プランに応じた回数制限あり)
                          </span>
                        </div>

                      </label>

                      {/* Option 3: Custom API */}
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="apiMode"
                          value="custom"
                          checked={apiMode === "custom"}
                          onChange={(e) => {
                            const val = e.target.value;
                            setApiMode(val);
                            apiModeRef.current = val;
                          }}
                          className="mt-0.5 accent-cyan-600"
                        />
                        <div className="w-full">
                          <span className="block text-xs font-bold text-slate-700">
                            ③ カスタムモード (Custom Key)
                          </span>
                          <span className="block text-[10px] text-slate-400 mb-1">
                            ご自身のAPIキーを利用 (回数無制限)
                          </span>

                          {apiMode === "custom" && (
                            <div className="animate-in slide-in-from-top-1 mt-1">
                              <input
                                type="password"
                                placeholder="Paste API Key here..."
                                value={customKey}
                                onChange={handleCustomKeyChange}
                                className="w-full text-xs p-2 border border-slate-300 rounded bg-white text-slate-700 focus:outline-cyan-500 placeholder:text-slate-300"
                              />
                              <div className="mt-2">
                                <label className="block text-[10px] text-slate-500 mb-1 font-bold">
                                  Model Selection
                                </label>
                                <select
                                  value={customModel}
                                  onChange={handleCustomModelChange}
                                  className="w-full text-xs p-2 border border-slate-300 rounded bg-white text-slate-700 focus:outline-cyan-500 cursor-pointer"
                                >
                                  <option value="gemini-2.5-flash-lite">
                                    Gemini 2.5 Flash Lite
                                  </option>
                                  <option value="gemini-2.5-flash">
                                    Gemini 2.5 Flash
                                  </option>
                                  <option value="gemini-2.5-pro">
                                    Gemini 2.5 Pro
                                  </option>
                                  <option value="gemini-3-flash-preview">
                                    Gemini 3 Flash Preview
                                  </option>
                                </select>
                              </div>
                              <p className="text-[9px] text-slate-400 mt-1">
                                ※ Key is saved in your browser locally.
                              </p>
                            </div>
                          )}
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
                
                {/* バージョン表記 */}
                <div className="mt-4 pt-2 border-t border-slate-100 text-center text-[10px] text-slate-300">
                  Version: v1.0.1
                </div>

              </div>
            </div>
          </div>
        )}

        {/* Content Area */}
        <div
          className={`flex-1 overflow-y-auto relative scrollbar-hide ${
            mode === "reading" ? "pb-6" : "pb-6"
          }`}
        >
          {/* === READING MODE === */}
          {mode === "reading" && (
            <div className="flex flex-col items-center min-h-full">
              {currentPageData.image ? (
                <div className="w-full max-h-[35vh] aspect-video relative group overflow-hidden bg-black shrink-0">
                  <img
                    src={currentPageData.image}
                    alt={currentPageData.title}
                    className="w-full h-full object-contain"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end justify-center pb-2">
                    <span className="text-white font-bold text-lg tracking-widest drop-shadow-md px-4 text-center">
                      {currentPageData.title}
                    </span>
                  </div>
                  {/* 透かしページ移動ボタン (画像あり) */}
                  <button
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 0}
                    className="absolute left-0 top-0 bottom-0 w-20 flex items-center justify-center z-20 group disabled:hidden"
                  >
                    <div className="w-10 h-10 rounded-full bg-black/20 hover:bg-black/50 flex items-center justify-center transition-all text-white/70 hover:text-white backdrop-blur-sm">
                      <ChevronLeft size={24} />
                    </div>
                  </button>
                  <button
                    onClick={() => handlePageChange(page + 1)}
                    className="absolute right-0 top-0 bottom-0 w-20 flex items-center justify-center z-20 group"
                  >
                    <div
                      className={`
                        w-10 h-10 rounded-full flex items-center justify-center transition-all backdrop-blur-sm shadow-lg
                        ${
                          isLastPage
                            ? "bg-black/20 text-white/70 hover:bg-black/50 hover:text-white"
                            : "bg-black/20 text-white/70 hover:bg-black/50 hover:text-white"
                        }
                      `}
                    >
                      {isLastPage ? (
                        <ListChecks size={20} />
                      ) : (
                        <ChevronRight size={24} />
                      )}
                    </div>
                  </button>
                </div>
              ) : (
                <div
                  className={`w-full max-h-[35vh] aspect-video ${currentPageData.color} flex items-center justify-center relative overflow-hidden group shrink-0`}
                >
                  <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
                  <div className="text-center z-10 px-6">
                    <BookOpen
                      size={48}
                      className="text-white/80 mx-auto mb-2"
                    />
                    <span className="text-white font-bold text-lg tracking-widest block drop-shadow-md">
                      {currentPageData.title}
                    </span>
                  </div>
                  {/* 透かしページ移動ボタン (画像なし) */}
                  <button
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 0}
                    className="absolute left-0 top-0 bottom-0 w-20 flex items-center justify-center z-20 group disabled:hidden"
                  >
                    <div className="w-10 h-10 rounded-full bg-black/10 hover:bg-black/30 flex items-center justify-center transition-all text-white/70 hover:text-white">
                      <ChevronLeft size={24} />
                    </div>
                  </button>
                  <button
                    onClick={() => handlePageChange(page + 1)}
                    className="absolute right-0 top-0 bottom-0 w-20 flex items-center justify-center z-20 group"
                  >
                    <div
                      className={`
                        w-10 h-10 rounded-full flex items-center justify-center transition-all backdrop-blur-sm shadow-lg
                        ${
                          isLastPage
                            ? "bg-black/10 text-white/70 hover:bg-black/30 hover:text-white"
                            : "bg-black/10 text-white/70 hover:bg-black/30 hover:text-white"
                        }
                      `}
                    >
                      {isLastPage ? (
                        <ListChecks size={20} />
                      ) : (
                        <ChevronRight size={24} />
                      )}
                    </div>
                  </button>
                </div>
              )}
              <div className="w-full px-4 sm:px-6 pt-6 pb-4 text-center space-y-4 sm:space-y-6">
                <div className="flex flex-wrap justify-center gap-x-1.5 gap-y-3 leading-relaxed text-lg sm:text-2xl font-medium text-slate-700">
                  {currentPageData.rubyText.map((wordObj, idx) => (
                    <React.Fragment key={idx}>
                      {wordObj.w === "\n" ? (
                        <div className="w-full h-0 basis-full" />
                      ) : (
                        <span
                          className={`relative group cursor-pointer hover:bg-amber-50 rounded px-0.5 transition-colors select-none ${
                            wordObj.s && showSlash ? "mr-1.5 sm:mr-2" : "mx-0"
                          }`}
                        >
                          <span
                            className={`absolute -top-3 left-0 text-[9px] text-amber-600 font-bold whitespace-nowrap transition-all duration-300 ${
                              showRuby
                                ? "opacity-100 translate-y-0"
                                : "opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0"
                            }`}
                          >
                            {wordObj.r}
                          </span>
                          {wordObj.w}
                          {wordObj.s && showSlash && (
                            <span className="absolute -right-2 sm:-right-2.5 top-1/2 -translate-y-1/2 text-amber-500 text-base font-bold select-none pointer-events-none">
                              /
                            </span>
                          )}
                        </span>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
              <div
                className={`w-full px-4 sm:px-6 transition-all duration-500 ease-out shrink-0 ${
                  showTrans || showGrammar
                    ? "opacity-100 translate-y-0 pb-4"
                    : "opacity-0 translate-y-4 h-0 overflow-hidden"
                }`}
              >
                <div className="bg-slate-50 rounded-2xl p-4 sm:p-5 border border-slate-100 shadow-sm text-sm space-y-3 sm:space-y-4">
                  {showTrans && (
                    <div className="pb-3 border-b border-slate-200">
                      <div className="flex items-center gap-2 mb-1 text-slate-400 text-xs font-bold uppercase tracking-wider">
                        <Languages size={12} /> Japanese
                      </div>
                      <p className="text-slate-700 font-medium leading-relaxed text-sm sm:text-base whitespace-pre-wrap">
                        {currentPageData.ja}
                      </p>
                    </div>
                  )}
                  {showGrammar && currentPageData.learning && (
                    <div className="animate-in slide-in-from-top-2">
                      <div className="flex items-center gap-2 mb-2 text-amber-600 font-bold text-xs uppercase tracking-wider">
                        <GraduationCap size={14} /> Learning Point
                      </div>
                      {currentPageData.learning.grammar && (
                        <div className="mb-3">
                          <div className="font-bold text-slate-700 mb-1">
                            {currentPageData.learning.grammar.title}
                          </div>
                          <p className="text-slate-500 text-xs leading-relaxed">
                            {currentPageData.learning.grammar.desc}
                          </p>
                        </div>
                      )}
                      {currentPageData.learning.vocab && (
                        <div className="flex flex-wrap gap-2">
                          {currentPageData.learning.vocab.map((v, i) => (
                            <span
                              key={i}
                              className="text-xs bg-white text-slate-600 px-2 py-1 rounded border border-slate-200 shadow-sm"
                            >
                              <span className="font-bold text-amber-600">
                                {v.word}
                              </span>{" "}
                              : {v.mean}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* === MENU MODE === */}
          {mode === "menu" && (
            <div className="p-8 min-h-full flex flex-col items-center justify-center text-center animate-in zoom-in-95 duration-500">
              <div className="relative mb-6 mt-2">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 bg-sky-200 rounded-full blur-xl opacity-50 animate-pulse"></div>
                <div className="w-20 h-20 bg-gradient-to-br from-sky-100 to-white rounded-full flex items-center justify-center relative shadow-lg border-4 border-white mx-auto">
                  <Dumbbell size={32} className="text-sky-500" />
                </div>
              </div>

              <h2 className="text-2xl font-black mb-2 text-slate-800">
                Practice Time!
              </h2>
              <p className="text-slate-500 mb-6 font-medium">
                英語力を定着させよう。
              </p>

              <div className="w-full space-y-4 max-w-xs">
                <button
                  onClick={() => startChallenge("quiz")}
                  className="w-full p-4 bg-white border-2 border-indigo-100 rounded-2xl flex items-center justify-between hover:border-indigo-500 hover:shadow-lg transition-all group"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-indigo-50 rounded-full flex items-center justify-center text-indigo-500 group-hover:bg-indigo-500 group-hover:text-white transition-colors">
                      <ListChecks size={20} />
                    </div>
                    <div className="text-left">
                      <div className="font-bold text-slate-700">
                        Quiz Challenge
                      </div>
                      <div className="text-xs text-slate-400">
                        内容理解クイズ ({quizCount}問)
                      </div>
                    </div>
                  </div>
                  <ArrowRight
                    size={20}
                    className="text-slate-300 group-hover:text-indigo-500"
                  />
                </button>

                <button
                  onClick={() => startChallenge("scramble")}
                  className="w-full p-4 bg-white border-2 border-emerald-100 rounded-2xl flex items-center justify-between hover:border-emerald-500 hover:shadow-lg transition-all group"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-50 rounded-full flex items-center justify-center text-emerald-500 group-hover:bg-emerald-500 group-hover:text-white transition-colors">
                      <Puzzle size={20} />
                    </div>
                    <div className="text-left">
                      <div className="font-bold text-slate-700">
                        Puzzle Challenge
                      </div>
                      <div className="text-xs text-slate-400">
                        並べ替えパズル ({scrambleCount}問)
                      </div>
                    </div>
                  </div>
                  <ArrowRight
                    size={20}
                    className="text-slate-300 group-hover:text-emerald-500"
                  />
                </button>

                <button
                  onClick={() => startChallenge("fill")}
                  className="w-full p-4 bg-white border-2 border-rose-100 rounded-2xl flex items-center justify-between hover:border-rose-500 hover:shadow-lg transition-all group"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-rose-50 rounded-full flex items-center justify-center text-rose-500 group-hover:bg-rose-500 group-hover:text-white transition-colors">
                      <PenTool size={20} />
                    </div>
                    <div className="text-left">
                      <div className="font-bold text-slate-700">
                        Grammar Challenge
                      </div>
                      <div className="text-xs text-slate-400">
                        穴埋め問題 ({fillCount}問)
                      </div>
                    </div>
                  </div>
                  <ArrowRight
                    size={20}
                    className="text-slate-300 group-hover:text-rose-500"
                  />
                </button>

                <button
                  onClick={() => startChallenge("speaking")}
                  className="w-full p-4 bg-white border-2 border-cyan-100 rounded-2xl flex items-center justify-between hover:border-cyan-500 hover:shadow-lg transition-all group"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-cyan-50 rounded-full flex items-center justify-center text-cyan-500 group-hover:bg-cyan-500 group-hover:text-white transition-colors">
                      <Mic size={20} />
                    </div>
                    <div className="text-left">
                      <div className="font-bold text-slate-700">
                        Speaking Challenge
                      </div>
                      <div className="text-xs text-slate-400">
                        発音練習 ({speakingCount}問)
                      </div>
                    </div>
                  </div>
                  <ArrowRight
                    size={20}
                    className="text-slate-300 group-hover:text-cyan-500"
                  />
                </button>
              </div>
            </div>
          )}

          {/* === QUIZ & FILL MODE === */}
          {(mode === "quiz" || mode === "fill") &&
            currentQuestions.length > 0 && (
              <div className="p-6 h-full flex flex-col animate-in slide-in-from-right duration-300">
                <div className="mb-4 bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex justify-between items-center">
                  <h2 className="text-lg font-bold flex items-center gap-2 text-slate-700">
                    {mode === "quiz" ? (
                      <ListChecks className="text-indigo-500" size={20} />
                    ) : (
                      <PenTool className="text-rose-500" size={20} />
                    )}
                    {mode === "quiz" ? "Quiz" : "Grammar"}{" "}
                    {currentQuestionIndex + 1}
                  </h2>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-400 bg-slate-100 px-2 py-1 rounded">
                      Score: {currentScore}
                    </span>
                    <span className="text-xs font-bold text-slate-400">
                      / {currentTotal}
                    </span>
                  </div>
                </div>

                <div
                  className={`flex-1 flex flex-col ${
                    mode === "fill" ? "justify-start pt-2" : "justify-center"
                  } mb-10`}
                >
                  <div
                    className={`text-center ${
                      mode === "fill" ? "mb-8" : "mb-4 mt-4"
                    }`}
                  >
                    {!isQuestionTextVisible && mode === "quiz" ? (
                      <div className="flex flex-col items-center justify-center py-0 animate-in zoom-in-95">
                        <button
                          onClick={playQuizQuestion}
                          className="w-20 h-20 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 shadow-lg hover:bg-indigo-200 active:scale-95 transition-all"
                        >
                          <Volume2 size={32} />
                        </button>
                        <p className="text-sm font-bold text-slate-500 mt-2">
                          Tap to listen
                        </p>
                        <button
                          onClick={() => setIsQuestionTextVisible(true)}
                          className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1 font-bold mt-2"
                        >
                          <Eye size={14} /> Show Text
                        </button>
                      </div>
                    ) : (
                      <div className="animate-in fade-in slide-in-from-bottom-2">
                        <h3 className="text-xl font-bold text-slate-800 leading-snug">
                          {currentQuestions[currentQuestionIndex].q}
                        </h3>
                        {mode === "quiz" && (
                          <div className="flex justify-center mt-2">
                            <button
                              onClick={playQuizQuestion}
                              className="text-indigo-500 hover:text-indigo-700 p-2 rounded-full hover:bg-indigo-50 transition-colors"
                            >
                              <Volume2 size={20} />
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="space-y-3">
                    {currentQuestions[currentQuestionIndex].options.map(
                      (option, idx) => {
                        let btnStyle =
                          "bg-white border-2 border-slate-100 text-slate-600 hover:border-slate-300";

                        if (mode === "quiz") {
                          if (selectedOption === idx)
                            btnStyle =
                              "bg-indigo-50 border-indigo-500 text-indigo-700 ring-2 ring-indigo-200";
                          if (quizSubmitted) {
                            if (
                              idx === currentQuestions[currentQuestionIndex].a
                            )
                              btnStyle =
                                "bg-green-50 border-green-500 text-green-700 shadow-md";
                            else if (selectedOption === idx)
                              btnStyle =
                                "bg-red-50 border-red-400 text-red-600";
                            else
                              btnStyle =
                                "bg-slate-50 border-slate-100 text-slate-300 opacity-50";
                          }
                        } else {
                          if (selectedOption === idx)
                            btnStyle =
                              "bg-rose-50 border-rose-500 text-rose-700 ring-2 ring-rose-200";
                          if (quizSubmitted) {
                            if (
                              idx === currentQuestions[currentQuestionIndex].a
                            )
                              btnStyle =
                                "bg-green-50 border-green-500 text-green-700 shadow-md";
                            else if (selectedOption === idx)
                              btnStyle =
                                "bg-red-50 border-red-400 text-red-600";
                            else
                              btnStyle =
                                "bg-slate-50 border-slate-100 text-slate-300 opacity-50";
                          }
                        }

                        return (
                          <button
                            key={idx}
                            onClick={() => handleOptionSelect(idx)}
                            disabled={quizSubmitted}
                            className={`w-full p-4 rounded-xl text-left transition-all font-bold text-lg flex justify-between items-center shadow-sm ${btnStyle}`}
                          >
                            {option}
                            {quizSubmitted &&
                              idx ===
                                currentQuestions[currentQuestionIndex].a && (
                                <CheckCircle
                                  className="text-green-600"
                                  size={24}
                                />
                              )}
                            {quizSubmitted &&
                              selectedOption === idx &&
                              idx !==
                                currentQuestions[currentQuestionIndex].a && (
                                <XCircle className="text-red-500" size={24} />
                              )}
                          </button>
                        );
                      }
                    )}
                  </div>

                  <div className="mt-6">
                    {!quizSubmitted ? (
                      <button
                        onClick={checkQuizAnswer}
                        disabled={selectedOption === null}
                        className={`w-full text-white py-4 rounded-xl font-bold shadow-lg active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                          mode === "quiz"
                            ? "bg-indigo-600 hover:bg-indigo-500"
                            : "bg-rose-600 hover:bg-rose-500"
                        }`}
                      >
                        Check Answer
                      </button>
                    ) : (
                      <div className="animate-in fade-in slide-in-from-bottom-4 duration-300">
                        <div
                          className={`p-5 rounded-2xl text-sm mb-4 border ${
                            isCorrect
                              ? "bg-green-50 border-green-100 text-green-800"
                              : "bg-red-50 border-red-100 text-red-800"
                          }`}
                        >
                          <p className="leading-relaxed opacity-90 font-bold">
                            {currentQuestions[currentQuestionIndex]
                              .originalExp ||
                              currentQuestions[currentQuestionIndex].exp}
                          </p>
                        </div>
                        <button
                          onClick={nextQuestion}
                          className={`w-full text-white py-4 rounded-xl font-bold shadow-xl active:scale-95 transition-all flex items-center justify-center gap-2 ${
                            mode === "quiz"
                              ? "bg-indigo-600 hover:bg-indigo-500"
                              : "bg-rose-600 hover:bg-rose-500"
                          }`}
                        >
                          {currentQuestionIndex < currentQuestions.length - 1
                            ? "Next Question"
                            : "See Results"}{" "}
                          <ArrowRight size={18} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-6 text-center">
                  <button
                    onClick={backToMenu}
                    className="text-slate-400 text-xs font-bold hover:text-slate-600 flex items-center justify-center gap-1 mx-auto"
                  >
                    <LogOut size={12} /> Exit Challenge
                  </button>
                </div>
              </div>
            )}

          {/* === SCRAMBLE MODE === */}
          {mode === "scramble" && currentQuestions.length > 0 && (
            <div className="p-6 h-full flex flex-col animate-in slide-in-from-right duration-300">
              <div className="mb-8 bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex justify-between items-center">
                <h2 className="text-lg font-bold flex items-center gap-2 text-slate-700">
                  <Puzzle className="text-emerald-500" size={20} />
                  Puzzle {currentQuestionIndex + 1}
                </h2>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-slate-400 bg-slate-100 px-2 py-1 rounded">
                    Score: {currentScore}
                  </span>
                  <span className="text-xs font-bold text-slate-400">
                    / {currentTotal}
                  </span>
                </div>
              </div>

              <div className="flex-1 flex flex-col justify-start mb-10">
                <div className="text-center mb-6">
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                    Translate this
                  </span>
                  <h3 className="text-lg font-bold mt-2 text-slate-800">
                    {currentQuestions[currentQuestionIndex].q}
                  </h3>
                </div>

                <div className="min-h-[60px] bg-slate-100 rounded-xl p-4 mb-2 flex flex-wrap gap-2 justify-center items-center border-2 border-slate-200 border-dashed">
                  {currentOrder.map((wordIdx, i) => (
                    <button
                      key={i}
                      onClick={() =>
                        !scrambleResult && handleWordClick(wordIdx)
                      }
                      className="bg-emerald-600 text-white px-3 py-1.5 rounded-lg font-bold shadow-md hover:bg-emerald-500 active:scale-95 transition-all animate-in zoom-in"
                    >
                      {currentQuestions[currentQuestionIndex].w[wordIdx]}
                    </button>
                  ))}
                  {currentOrder.length === 0 && (
                    <span className="text-slate-400 text-sm">
                      単語をタップして並べてください
                    </span>
                  )}
                </div>

                {currentOrder.length > 0 && !scrambleResult && (
                  <div className="flex justify-end mb-6">
                    <button
                      onClick={resetScrambleCurrent}
                      className="text-xs font-bold text-slate-400 flex items-center gap-1 hover:text-slate-600"
                    >
                      <RotateCcw size={12} /> Reset
                    </button>
                  </div>
                )}

                <div className="flex flex-wrap gap-2 justify-center mb-8">
                  {currentQuestions[currentQuestionIndex].displayOrder.map(
                    (wordIdx) => {
                      const isSelected = currentOrder.includes(wordIdx);
                      return (
                        <button
                          key={wordIdx}
                          onClick={() => handleWordClick(wordIdx)}
                          disabled={isSelected || scrambleResult !== null}
                          className={`px-3 py-2 rounded-lg font-medium border-2 transition-all ${
                            isSelected
                              ? "bg-slate-100 border-slate-100 text-slate-300 scale-95"
                              : "bg-white border-slate-200 text-slate-600 hover:border-emerald-300 hover:bg-emerald-50 shadow-sm"
                          }`}
                        >
                          {currentQuestions[currentQuestionIndex].w[wordIdx]}
                        </button>
                      );
                    }
                  )}
                </div>

                <div className="mt-auto">
                  {scrambleResult === null ? (
                    <button
                      onClick={checkScramble}
                      disabled={
                        currentOrder.length !==
                        currentQuestions[currentQuestionIndex].w.length
                      }
                      className="w-full bg-slate-800 text-white py-4 rounded-xl font-bold shadow-lg hover:bg-slate-700 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Check Answer
                    </button>
                  ) : (
                    <div className="animate-in slide-in-from-bottom-4">
                      <div
                        className={`p-4 rounded-xl text-center mb-4 font-bold text-lg flex flex-col items-center justify-center gap-2 ${
                          scrambleResult
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          {scrambleResult ? (
                            <>
                              <CheckCircle /> Correct!
                            </>
                          ) : (
                            <>
                              <XCircle /> Incorrect
                            </>
                          )}
                        </div>
                        {!scrambleResult && (
                          <div className="mt-2 text-sm font-normal text-slate-600 bg-white/50 px-3 py-2 rounded-lg w-full border border-red-100">
                            <span className="text-xs font-bold text-slate-400 uppercase block mb-1">
                              Correct Answer
                            </span>
                            {currentQuestions[currentQuestionIndex].w.join(" ")}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={nextScramble}
                        className="w-full bg-emerald-600 text-white py-4 rounded-xl font-bold shadow-lg hover:bg-emerald-500 active:scale-95 transition-all"
                      >
                        {currentQuestionIndex < currentQuestions.length - 1
                          ? "Next Puzzle"
                          : "See Results"}{" "}
                        <ArrowRight className="inline ml-1" size={18} />
                      </button>
                    </div>
                  )}
                </div>

                <div className="mt-8 text-center">
                  <button
                    onClick={backToMenu}
                    className="text-slate-400 text-xs font-bold hover:text-slate-600 flex items-center justify-center gap-1 mx-auto"
                  >
                    <LogOut size={12} /> Exit Challenge
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* === SPEAKING MODE === */}
          {mode === "speaking" && currentQuestions.length > 0 && (
            <div className="p-6 h-full flex flex-col animate-in slide-in-from-right duration-300">
              <div className="mb-8 bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex justify-between items-center">
                <h2 className="text-lg font-bold flex items-center gap-2 text-slate-700">
                  <Mic className="text-cyan-500" size={20} />
                  Speaking {currentQuestionIndex + 1}
                </h2>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-slate-400 bg-slate-100 px-2 py-1 rounded">
                    Score: {currentScore}
                  </span>
                  <span className="text-xs font-bold text-slate-400">
                    / {currentTotal}
                  </span>
                </div>
              </div>

              <div className="flex-1 flex flex-col justify-center mb-10">
                <div className="text-center space-y-6">
                  <div className="bg-slate-50 p-6 rounded-2xl border-2 border-dashed border-slate-200 relative group">
                    {/* ★追加: 文章更新ボタン */}
                    <button
                      onClick={refreshCurrentSpeakingQuestion}
                      title="Refresh sentence"
                      className="absolute top-2 right-2 p-1.5 bg-white rounded-full border border-slate-200 text-slate-400 hover:text-cyan-500 hover:border-cyan-200 shadow-sm opacity-0 group-hover:opacity-100 transition-all active:rotate-180"
                    >
                      <RefreshCw size={14} />
                    </button>

                    <p className="text-xl font-bold text-slate-700 mb-4 leading-relaxed">
                      {currentQuestions[currentQuestionIndex].text}
                    </p>

                    <div className="flex items-center justify-center gap-4">
                      <button
                        onClick={playSampleAudio}
                        className="text-sm font-bold text-cyan-600 flex items-center justify-center gap-2 hover:bg-cyan-50 py-2 px-4 rounded-full transition-colors"
                      >
                        <Volume2 size={16} /> Play Sample
                      </button>
                    </div>
                  </div>

                  {!speakingResult ? (
                    <div className="flex flex-col items-center">
                      <button
                        onClick={isRecording ? stopRecording : startRecording}
                        disabled={isAnalyzing}
                        className={`w-20 h-20 rounded-full flex items-center justify-center shadow-lg transition-all ${
                          isRecording
                            ? "bg-red-500 animate-pulse"
                            : isAnalyzing
                            ? "bg-slate-300 cursor-wait"
                            : "bg-cyan-500 hover:bg-cyan-600 hover:scale-105 active:scale-95"
                        }`}
                      >
                        {isAnalyzing ? (
                          <Loader2
                            size={32}
                            className="text-white animate-spin"
                          />
                        ) : isRecording ? (
                          <Square size={32} className="text-white fill-white" />
                        ) : (
                          <Mic size={32} className="text-white" />
                        )}
                      </button>
                      <p className="mt-4 text-sm font-bold text-slate-400">
                        {isRecording
                          ? "Tap to Stop"
                          : isAnalyzing
                          ? "Analyzing..."
                          : "Tap to Speak"}
                      </p>
                    </div>
                  ) : (
                    <div className="animate-in slide-in-from-bottom-4">
                      <div
                        className={`p-6 rounded-2xl border-2 mb-6 ${
                          speakingResult.score >= 4
                            ? "bg-green-50 border-green-200"
                            : speakingResult.score === 3
                            ? "bg-blue-50 border-blue-200"
                            : "bg-slate-50 border-slate-200"
                        }`}
                      >
                        <div className="flex justify-center mb-2">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              size={24}
                              className={
                                i < speakingResult.score
                                  ? "text-amber-400 fill-amber-400"
                                  : "text-slate-300 fill-transparent"
                              }
                            />
                          ))}
                        </div>
                        <div className="font-bold text-lg text-slate-800 mb-1 whitespace-pre-line">
                          {speakingResult.feedback}
                        </div>
                        <div className="text-xs text-slate-500 mt-2 border-t pt-2 border-slate-200/50">
                          Your input: "{transcript}"
                        </div>
                        {speakingResult.advice && (
                          <div className="text-sm text-slate-600 mt-2 border-t pt-2 border-slate-200/50 text-left bg-white/50 p-2 rounded">
                            <span className="font-bold text-xs text-amber-500 uppercase block mb-1">
                              Coach's Advice
                            </span>
                            {formatText(speakingResult.advice)}
                          </div>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <button
                          onClick={() => {
                            setSpeakingResult(null);
                            setTranscript("");
                          }}
                          className="py-3 px-4 rounded-xl font-bold border-2 border-slate-200 text-slate-600 hover:bg-slate-50 transition-all flex items-center justify-center gap-2"
                        >
                          <RotateCcw size={16} /> Retry
                        </button>
                        <button
                          onClick={nextQuestion}
                          className="py-3 px-4 rounded-xl font-bold bg-cyan-600 text-white hover:bg-cyan-500 shadow-lg active:scale-95 transition-all flex items-center justify-center gap-2"
                        >
                          Next <ArrowRight size={16} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-8 text-center">
                <button
                  onClick={backToMenu}
                  className="text-slate-400 text-xs font-bold hover:text-slate-600 flex items-center justify-center gap-1 mx-auto"
                >
                  <LogOut size={12} /> Exit Challenge
                </button>
              </div>
            </div>
          )}

          {/* === RESULT MODE === */}
          {mode === "result" && (
            <div className="p-8 min-h-full flex flex-col items-center justify-start text-center animate-in zoom-in-95 duration-500">
              <div className="pt-2 mb-2 relative">
                <div className="absolute top-8 left-0 right-0 bottom-0 bg-amber-200 rounded-full blur-xl opacity-40 animate-pulse"></div>
                <div className="w-20 h-20 bg-gradient-to-br from-amber-100 to-white rounded-full flex items-center justify-center relative shadow-lg border-4 border-white mx-auto">
                  <Trophy size={40} className="text-amber-500 drop-shadow-sm" />
                </div>
              </div>

              <h2
                className={`text-2xl font-black mb-1 ${resultContent?.color}`}
              >
                {resultContent?.title}
              </h2>
              <p className="text-slate-500 mb-4 font-medium">
                {resultContent?.msg}
              </p>

              <div className="w-full bg-white border border-slate-100 rounded-3xl shadow-xl relative">
                <div className="h-3 w-full bg-gradient-to-r from-amber-300 to-orange-400 rounded-t-3xl"></div>
                <div className="p-8 text-center">
                  <div className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">
                    Total Score
                  </div>
                  <div className="flex items-baseline justify-center gap-2">
                    <span className="text-6xl font-black text-slate-800 leading-normal">
                      {currentScore}
                    </span>
                    <span className="text-xl text-slate-400 font-bold">
                      / {currentTotal}
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-4 w-full max-w-xs mt-12 pb-12">
                <button
                  onClick={backToMenu}
                  className="w-full bg-slate-800 text-white py-4 rounded-xl font-bold shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all flex items-center justify-center gap-2"
                >
                  <ListChecks size={20} />
                  メニューに戻る
                </button>
              </div>
            </div>
          )}

          {/* === HELP MODE === */}
          {mode === "help" && (
            <div className="p-6 animate-in slide-in-from-right duration-300">
              <div className="mb-6 flex items-center gap-3">
                <button
                  onClick={backFromHelp}
                  className="p-2 -ml-2 rounded-full hover:bg-slate-100 text-slate-500 transition-colors"
                >
                  <ChevronLeft size={24} />
                </button>
                <h2 className="text-xl font-black text-slate-800 flex items-center gap-2">
                  <HelpCircle size={24} className="text-indigo-500" />
                  アプリの使い方
                </h2>
              </div>

              <div className="bg-indigo-50 p-4 rounded-xl border border-indigo-100 mb-6 text-sm text-slate-700 leading-relaxed shadow-sm">
                <p className="font-bold text-indigo-800 mb-2 flex items-center gap-2">
                  <Activity size={16} /> プランによる機能の違いについて
                </p>
                <p>
                  本アプリは, ご登録のアカウントプラン（Guest, Free,
                  Premium）によって,
                  <span className="font-bold text-amber-600">
                    {" "}
                    高品質音声 (Text-to-Speech){" "}
                  </span>
                  や
                  <span className="font-bold text-cyan-600"> AI発音評価 </span>
                  を利用できる回数が異なります。 詳細は設定メニューの{" "}
                  <span className="font-bold">Account Status</span>{" "}
                  をご確認ください。
                </p>
              </div>

              <div className="space-y-8 pr-2 pb-10">
                <div className="space-y-4">
                  <div>
                    <h3 className="font-bold text-slate-700 border-b border-slate-200 pb-1">
                      📖 物語を読む (Reading)
                    </h3>
                    <p className="text-sm text-slate-600 leading-relaxed mt-2">
                      英語の物語を読み進めながら学習します。画面下部のメニューボタンで以下の機能を切り替えられます。
                    </p>
                    <ul className="text-sm text-slate-600 list-disc list-inside space-y-1 ml-1 bg-slate-50 p-3 rounded-lg mt-2">
                      <li>
                        <span className="font-bold text-amber-600">Ruby</span>:
                        英単語の上にカタカナで読み方を表示します。
                      </li>
                      <li>
                        <span className="font-bold text-amber-600">Slash</span>:
                        英文の意味の区切りごとにスラッシュ(/)を表示します。
                      </li>
                      <li>
                        <span className="font-bold text-amber-600">JP</span>:
                        日本語訳を表示します。
                      </li>
                      <li>
                        <span className="font-bold text-amber-600">Tips</span>:
                        重要な単語や文法の解説を表示します。
                      </li>
                    </ul>
                    <p className="text-sm text-slate-600 mt-2">
                      <Volume2 size={14} className="inline mr-1" />{" "}
                      ボタンで音声を再生できます。再生速度や声の種類は設定メニューから変更可能です。
                    </p>
                  </div>

                  <div className="bg-amber-50 p-4 rounded-lg border border-amber-100">
                    <h4 className="font-bold text-amber-700 text-sm mb-2 flex items-center gap-2">
                      <Lightbulb size={16} /> 学習のヒント
                    </h4>
                    <div className="text-xs text-slate-700 space-y-4 leading-relaxed">
                      <div>
                        <span className="font-bold text-amber-600 block mb-1">
                          1. まずは「音」を聞く (Listen First)
                        </span>
                        <p>
                          英語は綴りと音が一致しないことが多い言語です。読み始める前に
                          <Volume2 size={12} className="inline" />
                          ボタンでネイティブの音声を聞き, 英語のリズムや強弱, 全体像を掴んでみましょう。音で理解することは, リーディングの大きな助けになります。
                        </p>
                      </div>
                      <div>
                        <span className="font-bold text-amber-600 block mb-1">
                          2. カタカナ読みでも「達成感」を (Success Experience)
                        </span>
                        <p>
                          「意味がわからない」以上に, 「読み方がわからず音声化できない」ことが挫折の大きな原因になります。
                          読めない単語があれば, 遠慮なく <b>Ruby</b>
                          （カタカナ）を使ってでも「最後まで読めた！」という達成感を味わうことが, 継続への近道です。
                        </p>
                      </div>
                      <div>
                        <span className="font-bold text-amber-600 block mb-1">
                          3. カタカナは「補助輪」 (Like Training Wheels)
                        </span>
                        <p>
                          カタカナ表記は自転車の補助輪のようなものです。初期段階では有効なサポートですが, 慣れてきたら少しずつOFFにして, 英語の音だけで読めるようにチャレンジしてみましょう。焦らず段階的に進めるのがコツです。
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <h3 className="font-bold text-slate-700 border-b border-slate-200 pb-1">
                    🏋️‍♀️ 練習メニュー (Practice)
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    物語を読み終えたら（またはいつでも左上のメニューから）, 以下の練習モードに挑戦できます。
                  </p>

                  <div className="grid gap-3">
                    <div className="bg-white border border-slate-200 p-3 rounded-lg shadow-sm">
                      <div className="flex items-center gap-2 font-bold text-indigo-600 mb-1">
                        <ListChecks size={16} /> Quiz Challenge
                      </div>
                      <p className="text-xs text-slate-500">
                        物語の内容に関する3択クイズです。
                        <br />
                        音声を聞いて答えを選びます。「Show
                        Text」で文字を表示することもできます。
                      </p>
                    </div>

                    <div className="bg-white border border-slate-200 p-3 rounded-lg shadow-sm">
                      <div className="flex items-center gap-2 font-bold text-emerald-600 mb-1">
                        <Puzzle size={16} /> Puzzle Challenge
                      </div>
                      <p className="text-xs text-slate-500">
                        バラバラになった単語を正しい順序に並べ替えて英文を完成させます。
                      </p>
                    </div>

                    <div className="bg-white border border-slate-200 p-3 rounded-lg shadow-sm">
                      <div className="flex items-center gap-2 font-bold text-rose-600 mb-1">
                        <PenTool size={16} /> Grammar Challenge
                      </div>
                      <p className="text-xs text-slate-500">
                        文法や前置詞などの穴埋め問題です。
                      </p>
                    </div>

                    <div className="bg-white border border-slate-200 p-3 rounded-lg shadow-sm">
                      <div className="flex items-center gap-2 font-bold text-cyan-600 mb-1">
                        <Mic size={16} /> Speaking Challenge
                      </div>
                      <p className="text-xs text-slate-500">
                        表示された英文をマイクに向かって読み上げてください。AIが発音を分析し, アドバイスを表示します。
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="font-bold text-slate-700 border-b border-slate-200 pb-1 flex items-center gap-2">
                    <Settings size={18} /> 設定 (Settings)
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    設定メニュー（歯車アイコン）から, アプリ全体の各種設定を一括で行えます。
                  </p>

                  <div className="bg-slate-50 p-4 rounded-lg space-y-4">
                    <div>
                      <h4 className="font-bold text-slate-700 text-sm mb-1">
                        👤 アカウントとAPI
                      </h4>
                      <ul className="text-xs text-slate-600 space-y-2">
                        <li>
                          <span className="font-bold text-slate-700">
                            Account Status
                          </span>
                          :
                          本日のAI機能（スピーキング評価）の残り回数を確認できます。
                          <div className="mt-2 pl-2 border-l-2 border-slate-300">
                            <p className="font-bold text-slate-600 mb-1">
                              利用回数の制限 (Standard Mode):
                            </p>
                            <ul className="list-disc list-inside ml-1 space-y-0.5 text-slate-500">
                              <li>
                                <span className="font-bold">Guest:</span>{" "}
                                1日5回（制限到達後は簡易判定モード）
                              </li>
                              <li>
                                <span className="font-bold">Free:</span>{" "}
                                1日10回（制限到達後は簡易判定モード）
                              </li>
                              <li>
                                <span className="font-bold">Premium:</span>{" "}
                                1日200回
                              </li>
                            </ul>
                          </div>
                        </li>
                        <li>
                          <span className="font-bold text-slate-700">
                            Text-to-Speech
                          </span>
                          : 物語の朗読機能です。
                          <div className="mt-1 pl-2 border-l-2 border-slate-300">
                            <ul className="list-disc list-inside ml-1 space-y-0.5 text-slate-500">
                              <li>
                                <span className="font-bold">Guest:</span>{" "}
                                ブラウザ標準音声のみ
                              </li>
                              <li>
                                <span className="font-bold">Free:</span>{" "}
                                高品質音声は1日10回まで (以降はブラウザ音声)
                              </li>
                              <li>
                                <span className="font-bold">Premium:</span>{" "}
                                高品質音声を無制限で利用可能
                              </li>
                            </ul>
                          </div>
                        </li>
                        <li>
                          <span className="font-bold text-slate-700">
                            API Mode
                          </span>
                          : スピーキング評価に使用するAIエンジンの設定です。
                          <ul className="list-none list-inside ml-2 mt-1 space-y-1">
                            <li>
                              <span className="font-bold">
                                ① APIを使用しない (No AI)
                              </span>
                              :
                              簡易発音判定を行います。
                            </li>
                            <li>
                              <span className="font-bold">
                                ② 標準モード (Standard)
                              </span>
                              : アプリ標準のAIを使用します。
                            </li>
                            <li>
                              <span className="font-bold">
                                ③ カスタムモード (Custom Key)
                              </span>
                              : ご自身のGemini APIキーを設定して使用します。
                            </li>
                          </ul>
                        </li>
                      </ul>
                    </div>

                    <div className="border-t border-slate-200 pt-3">
                      <h4 className="font-bold text-slate-700 text-sm mb-1">
                        📖 オーディオ設定
                      </h4>
                      <p className="text-xs text-slate-600">
                        <span className="font-bold text-slate-700">
                          Audio Settings
                        </span>
                        :
                        読み上げ音声の声質や再生速度を調整できます。
                      </p>
                    </div>

                    <div className="border-t border-slate-200 pt-3">
                      <h4 className="font-bold text-slate-700 text-sm mb-1">
                        🏋️‍♀️ 練習設定
                      </h4>
                      <p className="text-xs text-slate-600 mb-2">
                        <span className="font-bold text-slate-700">
                          Challenge Settings
                        </span>
                        : 各チャレンジモードの問題数を変更できます。
                      </p>
                    </div>
                  </div>
                </div>

                <div className="pt-4 text-center">
                  <button
                    onClick={backFromHelp}
                    className="bg-slate-800 text-white px-6 py-3 rounded-xl font-bold shadow-lg hover:bg-slate-700 transition-colors"
                  >
                    元の画面に戻る
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Area (Reading Mode Only) */}
        {mode === "reading" && (
          <div className="w-full px-4 py-4 bg-white border-t border-slate-100 z-30 shrink-0">
            {/* Voice Type Indicator (New) */}
            {isPlaying && (
              <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-full mb-2 bg-slate-800/90 text-white text-[10px] px-3 py-1 rounded-full backdrop-blur-sm animate-in fade-in slide-in-from-bottom-1 flex items-center gap-2 shadow-sm whitespace-nowrap">
                {activeVoiceType === "cloud" ? (
                  <>
                    <Sparkles size={10} className="text-amber-400" /> Premium
                    Voice (Cloud)
                  </>
                ) : (
                  <>
                    <WifiOff size={10} className="text-slate-400" /> Standard
                    Voice (Browser)
                  </>
                )}
              </div>
            )}

            <div className="grid grid-cols-6 gap-2">
              <div
                className={`col-span-2 flex items-stretch rounded-xl overflow-hidden shadow-sm border ${
                  isPlaying || isTTSLoading
                    ? "border-slate-800 bg-slate-800"
                    : "border-slate-100 bg-slate-50"
                }`}
              >
                <button
                  onClick={() => handlePlayPause()}
                  className={`flex-1 py-2 text-[9px] font-bold flex flex-col items-center justify-center gap-1 transition-colors ${
                    isPlaying || isTTSLoading
                      ? "text-white"
                      : "text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  {isTTSLoading ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : isPlaying ? (
                    <div className="relative">
                      <Volume2 size={14} className="animate-pulse" />
                    </div>
                  ) : isPaused ? (
                    <Play size={14} className="ml-0.5" />
                  ) : (
                    <Volume2 size={14} />
                  )}
                  
                  <span>
                    {isTTSLoading
                      ? "Loading..."
                      : isPlaying
                      ? "Playing"
                      : isPaused
                      ? "Paused"
                      : "Listen"}
                  </span>
                </button>
                <div
                  className={`w-px ${
                    isPlaying || isTTSLoading ? "bg-slate-700" : "bg-slate-200"
                  }`}
                ></div>
                <button
                  onClick={() => handleStop()}
                  disabled={!isPlaying && !isPaused && !isTTSLoading}
                  className={`px-3 py-2 text-[9px] font-bold flex flex-col items-center justify-center gap-1 transition-colors ${
                    isPlaying || isPaused || isTTSLoading
                      ? (isPlaying || isTTSLoading ? "text-slate-300" : "text-slate-500") + " hover:text-white hover:bg-slate-700 cursor-pointer"
                      : "text-slate-300 opacity-50 cursor-not-allowed"
                  }`}
                >
                  <Square size={14} fill="currentColor" />
                  <span>Stop</span>
                </button>
              </div>

              <button
                onClick={() => setShowRuby(!showRuby)}
                className={`col-span-1 py-2 rounded-xl text-[9px] font-bold flex flex-col items-center gap-1 border ${
                  showRuby
                    ? "bg-amber-50 text-amber-600 border-amber-100"
                    : "bg-slate-50 text-slate-400 border-transparent hover:bg-slate-100"
                }`}
              >
                <Type size={14} /> Ruby
              </button>
              <button
                onClick={() => setShowSlash(!showSlash)}
                className={`col-span-1 py-2 rounded-xl text-[9px] font-bold flex flex-col items-center gap-1 border ${
                  showSlash
                    ? "bg-amber-50 text-amber-600 border-amber-100"
                    : "bg-slate-50 text-slate-400 border-transparent hover:bg-slate-100"
                }`}
              >
                <Split size={14} /> Slash
              </button>
              <button
                onClick={() => setShowTrans(!showTrans)}
                className={`col-span-1 py-2 rounded-xl text-[9px] font-bold flex flex-col items-center gap-1 border ${
                  showTrans
                    ? "bg-amber-50 text-amber-600 border-amber-100"
                    : "bg-slate-50 text-slate-400 border-transparent hover:bg-slate-100"
                }`}
              >
                <Languages size={14} /> JP
              </button>
              <button
                onClick={() => setShowGrammar(!showGrammar)}
                className={`col-span-1 py-2 rounded-xl text-[9px] font-bold flex flex-col items-center gap-1 border ${
                  showGrammar
                    ? "bg-amber-50 text-amber-600 border-amber-100"
                    : "bg-slate-50 text-slate-400 border-transparent hover:bg-slate-100"
                }`}
              >
                <Lightbulb size={14} /> Tips
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
